import { Fragment, useState, useEffect, useCallback, useMemo, useRef, type ComponentType, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Bug, GripVertical, ChevronDown, Trash2, Plus, Loader2, ShieldCheck, Zap, X, AlertTriangle, Link2, Lock, Check, Eye, Pencil, Ban, Share2, Play, Pause, Palette, RefreshCw, Clock } from 'lucide-react'
import { useTheme, getThemeClass, FileIcon, ErrorBoundary, MiniappIcon, useConfirmOptional, useAnchoredPosition } from '@papan/core-ui/ui'
import { hintAttrs } from '@papan/core-ui/ui/tooltipHint'
import { ConsentItemLabel } from './ConsentItemLabel.js'
import { AppEnvSettings } from '@papan/keybook/ui/app-env'
import { useTranslation } from '@papan/i18n'
import { useAppWrites } from '@papan/files/client/queries'
import { AppDatabaseWrites, writesNeedingAttention } from './AppDatabaseWrites.js'
import { DebugLogViewer, MiniappIconEditorDialog } from '@papan/files/ui'
import { getActiveTenantId, errorText } from '@papan/api-client'
import { useRealtimeOptional, type FileEvent } from '@papan/realtime/client'
import { PickerNavigator, getRootFolderId, readableName, type PickerRoot, type PickerCrumb, type PickerSelection } from '@papan/files/ui/picker'
import { miniappApi, filesApi, chainRedundancy, type MiniAppGrant, type MiniAppChain, type FileListItem, type Trigger, type Schedule, type MiniAppConsentItem, type MiniAppConsentDecision } from '@papan/files/client'
import { MINIAPP_ICON_METADATA_KEY, MIN_SCHEDULE_SECONDS, SCHEDULE_DEFAULT_TZ, describeCronSchedule, isPlausibleCron, isValidTimeZone, scheduleTimeZone, stripMiniappFolderExt, createAccessBurst, isChainPointer, type MiniappIconSpec } from '@papan/types'
import { ScheduleNextRuns, browserTimeZone } from './ScheduleNextRuns.js'
import { planRevokeConfirm } from './revoke-confirm.js'

/**
 * MiniAppSettingsModifier — content of the MiniApp editor (toolbar + wifi chrome
 * live in MiniAppEditor). Only loads for a caller who passes the Modification
 * Safeguard (server-gated: write on the .miniapp folder AND user.access ≥ app.access
 * on every granted folder) — a 403 renders the "can't manage" state.
 *
 * Sections:
 *  1. App access  — the OWN folder pinned first (always shown; No Access + inline
 *     read+ warning when ungranted; never deletable), then the app-principal's external
 *     grants, each with an inline permission dropdown. Through a chain an app is like a user
 *     through a chain (owner, 2026-10-20): placement is the only cap — effective = min(grant,
 *     the app's own level on the chain row, `chainPlacement`) — so a level is shown capped only
 *     where that row level is lower (e.g. "Capped to Read" when the app only reads its chain).
 *     Inline warning on an external grant with no matching `.chain` (the folder isn't in the
 *     app's context yet).
 *  2. Chains      — every `.chain` inside the .miniapp folder. An orphan chain (target
 *     the app has no grant on) carries its warning inline, right under the chain.
 *  3. Triggers    — placeholder (management lands with the triggers PR).
 */

interface StatusPayload { loaded: boolean; saving: boolean; error: boolean }
/** Where a failed action's message shows — beside the control that was used (#1544: a refused trigger save showed
 *  under App access, far from the button). */
type ErrorArea = 'access' | 'consent' | 'triggers' | 'trigger-editor' | 'schedules' | 'schedule-editor' | 'preview'

interface MiniAppSettingsModifierProps {
  folderId: string
  folderName?: string
  onStatusChange?: (s: StatusPayload) => void
  /** When true, don't render the not-installed "needs your permission" screen — the runner
   *  owns the install consent point (settings is for configuring an installed
   *  app). Settings then renders normally even before install (own-folder shows
   *  No Access). */
  suppressInstallGate?: boolean
  /** Reports auto-refresh toggle changes so the host (MiniAppEditor) can apply
   *  them to the running app live, without waiting for a re-fetch. */
  onAutoRefreshChange?: (autoRefresh: boolean) => void
}

const PERM_LABEL: Record<string, string> = { none: 'No Access', readcopy: 'Read-only', readwrite: 'Read & Write', admin: 'Manage & Edit' }
const PERM_ORDER: string[] = ['admin', 'readwrite', 'readcopy']
const PERM_ICON: Record<string, ComponentType<{ size?: number; className?: string }>> = { admin: ShieldCheck, readwrite: Pencil, readcopy: Eye, none: Ban }
const PERM_DESC: Record<string, string> = {
  admin: 'Manage sharing and edit content.',
  readwrite: 'Read and write content.',
  readcopy: 'Read content only.',
  none: 'No access to this folder.',
}

/** One ordering of access levels for this screen ('hidden' = No access). */
const LEVEL_RANK: Record<string, number> = { none: 0, hidden: 0, readcopy: 1, readwrite: 2, admin: 3, owner: 4 }
const levelRank = (p: string | undefined): number => LEVEL_RANK[p ?? 'none'] ?? 0

export function MiniAppSettingsModifier({ folderId, folderName, onStatusChange, suppressInstallGate, onAutoRefreshChange }: MiniAppSettingsModifierProps) {
  const [loadNonce, setLoadNonce] = useState(0)   // #1316: bumped to ask the load again after a dropped answer
  const [access, setAccess] = useState<MiniAppGrant[]>([])
  const [chains, setChains] = useState<MiniAppChain[]>([])
  // The server checked only the first chains of a very large app (#1235): the caps shown may be incomplete.
  const [chainsIncomplete, setChainsIncomplete] = useState(false)
  const [triggers, setTriggers] = useState<Trigger[]>([])
  // Master trigger pause. The dispatcher folds a legacy `paused:true` into per-trigger
  // `enabled` on load (below), so this stays `false` in the UI and is written through
  // as-is on every save — schedules pause independently via `schedulesPaused`.
  // Deliberately constant: the legacy master `paused` is folded into per-trigger `enabled` on
  // load and always written back as false — there is no UI setter, so a `useState` here only
  // invites a future `setPaused(true)` that would round-trip a flag the UI normalizes away.
  const paused = false
  const [schedules, setSchedules] = useState<Schedule[]>([])
  // Per-schedule approval state from the API (null = an older API that doesn't send it).
  const [scheduleConsent, setScheduleConsent] = useState<Record<string, 'allowed' | 'paused' | 'denied' | 'pending' | 'invalid'> | null>(null)
  const [schedulesPaused, setSchedulesPaused] = useState(false)
  // #1544: what the triggers reader reports (an unreadable settings.json, old on/off values left in triggers.json).
  const [triggerProblems, setTriggerProblems] = useState<string[]>([])
  const [counts, setCounts] = useState<Record<string, { count: number; since: number }>>({})
  const [editingTrigger, setEditingTrigger] = useState<{ index: number | null } | null>(null)
  const [editingSchedule, setEditingSchedule] = useState<{ index: number | null } | null>(null)
  // read by withBusy's catch: an editor closed while its save was in flight shows the refusal in its section instead
  const editingTriggerRef = useRef(editingTrigger); editingTriggerRef.current = editingTrigger
  const editingScheduleRef = useRef(editingSchedule); editingScheduleRef.current = editingSchedule
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorArea, setErrorArea] = useState<ErrorArea>('access')
  // a failed LOAD, kept apart from an action's error: shown under App access whichever area an action's error is in
  const [loadError, setLoadError] = useState<string | null>(null)
  // D18: where the last grant was narrowed to the person's own access (a count — never names).
  const [notice, setNotice] = useState<string | null>(null)
  const noteMirrored = (m?: Array<{ folderId: string }>) => setNotice(m && m.length > 0
    ? `The app was limited to your own access in ${m.length} place${m.length === 1 ? '' : 's'} below — it can't reach what you can't.`
    : null)
  const [denied, setDenied] = useState<string | null>(null)
  // Can this viewer EDIT the app now (server: write on its folder + covers its reach)? false = the
  // overlay; refreshed on every reload, so it appears/clears live as access changes (realtime).
  const [canManageApp, setCanManageApp] = useState(true)
  const { settings } = useTheme()
  // A manager of the app folder who can't edit it may still lower / remove access — they can step
  // past the overlay for that; reset whenever editing comes back.
  const [overlayDismissed, setOverlayDismissed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerPerm, setPickerPerm] = useState('readcopy')
  const [showDebugLog, setShowDebugLog] = useState(false)
  // The debug log opened from a "database writes failed" row starts on its Runs lane.
  const [debugLogOnRuns, setDebugLogOnRuns] = useState(false)
  // Each run source's last write outcome (only an editor of the app gets it — this page is theirs).
  const appWrites = useAppWrites(folderId)
  const [consentOutstanding, setConsentOutstanding] = useState<MiniAppConsentItem[]>([])
  const [consentDecisions, setConsentDecisions] = useState<MiniAppConsentDecision[]>([])
  // Custom icon spec — read-visibility (weaker than manage), fetched on mount so
  // the preview matches Drive. `null` → the name-derived default renders.
  const [iconSpec, setIconSpec] = useState<MiniappIconSpec | null>(null)
  // Without the rev an IMAGE icon can't load its composited PNG and falls back to
  // the name-derived gradient — i.e. the app's own settings pane would misreport
  // the icon the user just set.
  const [iconRev, setIconRev] = useState<string | null>(null)
  const [iconEditorOpen, setIconEditorOpen] = useState(false)
  // Preview auto-refresh preference — read-visibility fetch on mount (defaults
  // on). Persisted independently of the bulk settings load (like the icon).
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Bumped on every reload of the panel — Variables and secrets reloads with it (its env.json changes from the setup,
  // the review dialog, another tab or a hand edit; it would otherwise show what it loaded first).
  const [envReload, setEnvReload] = useState(0)
  // Display only: a key pick's "Choose a key" asks Variables and secrets (on this page) to open that secret's editor.
  const [envEditRequest, setEnvEditRequest] = useState<{ name: string; n: number } | undefined>(undefined)
  const { t: tk } = useTranslation('keybook')
  const load = useCallback(async () => {
    setEnvReload(n => n + 1)
    try {
      const { access, chains, canManageApp: canManage, deepChainsIncomplete } = await miniappApi.listAccess(folderId)
      setChainsIncomplete(deepChainsIncomplete === true)
      // Absent (an older API) = can manage, as before.
      setCanManageApp(canManage !== false)
      if (canManage !== false) setOverlayDismissed(false)
      const { paused, triggers, schedules, schedulesPaused, counts, scheduleConsent, problems } = await miniappApi.getTriggers(folderId).catch(() => ({ paused: false, triggers: [] as Trigger[], schedules: [] as Schedule[], schedulesPaused: false, counts: {} as Record<string, { count: number; since: number }>, scheduleConsent: undefined, problems: undefined }))
      // Per-trigger `enabled` is the single source of truth for run/pause. A legacy
      // master `paused: true` (dormant kill-switch) is folded into the per-trigger
      // view so the UI never desyncs from what the dispatcher actually runs — and
      // `paused` is normalized back to false so subsequent saves write it that way.
      setAccess(access); setChains(chains)
      setTriggers(paused ? triggers.map(t => ({ ...t, enabled: false })) : triggers)
      // `?? []`/`?? false`: an OLD API instance (SPA/API deploy independently; rolling
      // restart) returns a success response WITHOUT these fields — `schedules.length`
      // then throws and the error boundary blanks the whole settings panel.
      setSchedules(schedules ?? []); setSchedulesPaused(schedulesPaused ?? false)
      setScheduleConsent(scheduleConsent ?? null)
      setTriggerProblems(problems ?? [])
      setCounts(counts); setLoaded(true); setError(null); setLoadError(null); setDenied(null)
      // Per-item consent (best-effort — a failure here shouldn't blank the page).
      try {
        const { outstanding, decisions } = await miniappApi.getConsentOutstanding(folderId)
        setConsentOutstanding(outstanding); setConsentDecisions(decisions)
      } catch { /* leave prior consent state */ }
      onStatusChange?.({ loaded: true, saving: false, error: false })
    } catch (e: any) {
      const text = errorText(e)
      // #1316: a dropped answer (its session ended) is asked again as the session now here — never an error. Its own
      // nonce, not `useDroppedLoadReask`: it also renders in the share view's Settings tab (SharePdappSettings), but its
      // routes ignore the share-session header and it remounts with the identity, so a re-ask is never as the ended sid.
      if (text === null) { setLoadNonce(n => n + 1); return }
      const msg = text
      // The safeguard/write gate returns 403 — render a "can't manage" state, not an error.
      if (/403|modification_safeguard|Write access|manage this (miniapp|PDApp)/i.test(msg)) {
        setDenied('You need write access on this PDApp folder — and at least the app’s own access on every folder it can reach — to manage it.')
        onStatusChange?.({ loaded: true, saving: false, error: false })
      } else {
        setLoadError(msg); onStatusChange?.({ loaded: false, saving: false, error: true })
      }
    }
  }, [folderId, onStatusChange])

  useEffect(() => { load() }, [load, loadNonce])

  // ── Live sync with EXTERNAL changes (the reverse of our own mutations) ──
  // The settings view must reflect changes it didn't make: a `.chain` added or
  // removed in the app folder via the drive, the `_appsystem/` folder or its
  // triggers.json created/edited in the editor, a granted folder renamed, etc.
  // All of these are file events under the app-folder subtree, so we reload
  // (debounced) whenever one touches an id we currently display. Our OWN
  // mutations already reload via withBusy, so we skip while a mutation is
  // in-flight to avoid a redundant double-fetch.
  const rt = useRealtimeOptional()
  const busyRef = useRef(false)
  const confirmDialog = useConfirmOptional()
  busyRef.current = busy
  // Ids whose file events should trigger a reload: the app folder itself, every
  // granted folder (including the internal `_appsystem/` config folder — a
  // triggers.json write carries parentId === the config folder), and every
  // chain plus its target (a chain DEEPER in the app reloads by its `.chain` event, below). Rebuilt each render from the live state via a ref so
  // the long-lived subscription handler always reads the current set.
  const relevantIdsRef = useRef<Set<string>>(new Set())
  relevantIdsRef.current = new Set<string>([
    folderId,
    ...access.map(a => a.folderId),
    ...chains.flatMap(ch => [ch.chainId, ch.targetFileId].filter((x): x is string => !!x)),
  ])
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Depend on the STABLE onFileEvent callback, not the whole `rt` context value:
  // the provider recreates that object every render (presence/status ticks), so
  // subscribing off `rt` would tear down + resubscribe constantly.
  const onFileEvent = rt?.onFileEvent
  const onPermissionChanged = rt?.onPermissionChanged
  useEffect(() => {
    if (!onFileEvent && !onPermissionChanged) return
    const scheduleReload = () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
      reloadTimerRef.current = setTimeout(() => { void load() }, 300)
    }
    const offFile = onFileEvent?.((event: FileEvent) => {
      if (busyRef.current) return // our own mutation's reload will pick it up
      const ids = relevantIdsRef.current
      // The app folder is never the drive root, so its children's events carry a
      // real parentId — no root normalization needed. file:deleted omits `file`
      // but still carries fileId, so a removed chain is caught by fileId.
      // A `.chain` event anywhere also reloads (#1235): a chain created DEEPER in the app (`data/r.chain`) caps a row,
      // and its parent folder isn't in the set. Debounced; a chain elsewhere in the drive costs one extra reload.
      // Accepted (display only): a FOLDER holding a deeper chain binned / moved out publishes one event for the
      // folder, not the chain, and a deeper chain renamed off its `.chain` ending is named by its NEW name — the
      // row's cap stays until the next reload (any permission change, reopening).
      const hit =
        event.parentId === folderId ||
        (event.parentId != null && ids.has(event.parentId)) ||
        ids.has(event.fileId) ||
        (!!event.name && isChainPointer(event.name))
      if (!hit) return
      scheduleReload()
    })
    // Grants and consent decisions are DB rows, not files — they arrive only as a
    // permission change naming the app (or a folder it reaches).
    // ANY permission change re-checks (owner, 2026-10-03): whether THIS viewer can still edit the app
    // depends on the app's grants (any of them, from either screen) AND the viewer's own access on
    // every folder the app reaches — including folders below its grants that aren't listed here.
    // So the "can't edit" overlay appears and clears live. At most one reload per interval
    // (`createAccessBurst`): an admin's run of team edits is one change per click, in every tab.
    // Busy when it is due (our own mutation running — which reloads only if it SUCCEEDS): ask again
    // after the interval instead of dropping it.
    const permBurst = createAccessBurst(() => { if (busyRef.current) permBurst.push(); else void load() })
    const offPerm = onPermissionChanged?.(() => permBurst.push())
    return () => {
      offFile?.()
      offPerm?.()
      permBurst.cancel()
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    }
  }, [onFileEvent, onPermissionChanged, folderId, load])

  // Custom icon — independent read-only fetch (works even without manage).
  useEffect(() => {
    let alive = true
    miniappApi.getIcon(folderId).then(({ icon, rev }) => {
      if (!alive) return
      setIconSpec(icon)
      setIconRev(rev ?? null)
    }).catch(() => {})
    return () => { alive = false }
  }, [folderId])

  // Auto-refresh preference — same independent read-visibility fetch.
  useEffect(() => {
    let alive = true
    miniappApi.getAutoRefresh(folderId).then(({ autoRefresh }) => { if (alive) setAutoRefresh(autoRefresh) }).catch(() => {})
    return () => { alive = false }
  }, [folderId])

  // Toggle + persist. Optimistic (+ live-apply to the runner via the callback);
  // revert on failure. Not routed through withBusy — it's independent of the
  // bulk settings and shouldn't trigger a full reload.
  const toggleAutoRefresh = useCallback(() => {
    const next = !autoRefresh
    setAutoRefresh(next)
    onAutoRefreshChange?.(next)
    miniappApi.setAutoRefresh(folderId, next).catch((e) => {
      setAutoRefresh(!next)
      onAutoRefreshChange?.(!next)
      const text = errorText(e)   // #1316: a dropped answer shows nothing
      if (text !== null) { setError(text); setErrorArea('preview') }
    })
  }, [autoRefresh, folderId, onAutoRefreshChange])

  const withBusy = useCallback(async (fn: () => Promise<void>, area: ErrorArea = 'access') => {
    setBusy(true); onStatusChange?.({ loaded, saving: true, error: false })
    // the action landed: its earlier refusal goes now, even if the reload after it fails
    try { await fn(); setError(null); await load() }
    catch (e) {
      const text = errorText(e)   // #1316: a dropped answer shows nothing (no error, no failed status)
      if (text !== null) {
        const shown: ErrorArea = area === 'trigger-editor' && !editingTriggerRef.current ? 'triggers'
          : area === 'schedule-editor' && !editingScheduleRef.current ? 'schedules'
          : area
        setError(text); setErrorArea(shown)
      }
      onStatusChange?.({ loaded, saving: false, error: text !== null })
    }
    finally { setBusy(false) }
  }, [load, loaded, onStatusChange])

  const grant = (pickedId: string, permission: string) => withBusy(async () => {
    // The grant first, WITHOUT a chain: the server resolves what was picked to the REAL folder (a
    // folder browsed to through a `.chain` arrives as a route id), and the chain decision below is
    // made on that real folder — else a folder an existing chain already reaches got a second one.
    const res = await miniappApi.grantAccess(folderId, pickedId, permission, { skipChain: true })
    noteMirrored(res.mirrored)
    const targetFolderId = res.folderId ?? pickedId
    // Don't auto-create a chain the app doesn't need: its own folder, a folder
    // INSIDE it, or one that already has a chain — the app reaches all of those
    // already (a chained PARENT does not count: a chain is a pointer, not a
    // permission — see chain-rules.ts), and the extra pointer is
    // permanent clutter in the app folder (chain-rules.ts). Breadcrumbs give the
    // ancestors in one call; if that read fails we fall back to the old
    // exact-duplicate check rather than skipping a chain the app needs.
    const ancestorIds = await filesApi.getBreadcrumbs(targetFolderId)
      .then(crumbs => crumbs.map(c => c.id))
      .catch(() => [targetFolderId])
    const { redundant } = chainRedundancy({
      appFolderId: folderId,
      targetFolderId,
      targetAncestorIds: ancestorIds,
      // The app folder's OWN chains only, as before #1235: a deeper chain can sit where the app's level is lower (a
      // read-only `_appsystem/`, a No-access limit, Needs approval) — it caps the grant, so it never makes a direct
      // chain redundant. A direct chain on top of a deeper one only adds the app's full route.
      chainedTargetIds: chains.map(ch => ch.targetFileId),
    })
    if (targetFolderId !== folderId && !redundant) {
      // Best-effort, as before: the grant is authoritative; a missing chain shows the "no chain" warning.
      await filesApi.createChain(folderId, targetFolderId).catch((err) => console.warn('[miniapp] auto-chain failed (grant persisted):', err))
    }
    setPickerOpen(false)
  })
  // An access row's dropdown selection. 'none' = No Access (revoke the grant, keep the
  // chain so it stays chained-but-unreadable); a tier = grant/adjust at that level.
  // "No Access" on a row under another grant (a limit, or one with an ancestor grant) writes No
  // access ON that folder — revoking it would INHERIT the broader grant instead (D20).
  const setGrant = (g: MiniAppGrant, permission: string) => withBusy(() =>
    permission === 'none' && !g.limitOf && !(g.groupCandidates?.length)
      ? miniappApi.revokeAccess(folderId, g.folderId, { keepChain: true })
      : miniappApi.setPermission(folderId, g.folderId, permission === 'none' ? 'hidden' : permission).then((r) => noteMirrored(r.mirrored)))
  // Re-sharing toggle on an existing grant (permission preserved).
  const setShare = (g: MiniAppGrant, allow: boolean) => withBusy(() => miniappApi.setSharing(folderId, g.folderId, allow).then(() => {}))
  // "Remove access" — revoke the grant AND delete its chain (full removal).
  const removeGrant = (g: MiniAppGrant) => withBusy(() => miniappApi.revokeAccess(folderId, g.folderId))
  // Own folder: grant/adjust self access (no chain — it's the app's own folder); 'none' revokes.
  const setOwnPerm = (permission: string) => withBusy(() =>
    permission === 'none'
      ? miniappApi.revokeAccess(folderId, folderId)
      : miniappApi.setPermission(folderId, folderId, permission).then((r) => noteMirrored(r.mirrored)))
  const addChain = (targetFolderId: string) => withBusy(() => filesApi.createChain(folderId, targetFolderId).then(() => {}))
  // Inline, not the delete job: a `.chain` is one pointer file, never a big tree (#921).
  const removeChain = (chainId: string) => withBusy(() => filesApi.deleteFile(chainId).then(() => {}))
  // Install — establish the app principal (readwrite self-grant + `_appsystem/` readonly).
  // The app doesn't run until this happens; it's the explicit consent point.
  const install = () => withBusy(() => miniappApi.install(folderId).then((r) => noteMirrored(r.mirrored)))

  // Per-item consent: allow/deny an outstanding item, or pause/resume an allowed trigger.
  const decideConsent = (item: MiniAppConsentItem, decision: 'allowed' | 'denied') =>
    withBusy(() => miniappApi.decideConsent(folderId, { itemType: item.itemType, contentHash: item.contentHash, decision, label: item.label, targetFolderId: item.targetFolderId }).then(() => {}), 'consent')
  const pauseConsentItem = (contentHash: string, paused: boolean) =>
    withBusy(() => miniappApi.pauseConsent(folderId, contentHash, paused).then(() => {}), 'consent')

  // saveTriggers is a WHOLE-FILE replace, so EVERY save must carry the current
  // triggers AND schedules (and both pause flags) or it wipes the untouched half.
  // Route every mutation through this merge helper: pass only what changes, the
  // rest comes from live state.
  const persist = (next: { triggers?: Trigger[]; paused?: boolean; schedules?: Schedule[]; schedulesPaused?: boolean }) =>
    miniappApi.saveTriggers(folderId, {
      triggers: next.triggers ?? triggers,
      paused: next.paused ?? paused,
      schedules: next.schedules ?? schedules,
      schedulesPaused: next.schedulesPaused ?? schedulesPaused,
    }).then(() => {})

  // Triggers: save the whole list (upsert at index, or append when index is null).
  // New triggers get a stable id (keys the fire counter); edits preserve id +
  // enabled state. Run/pause is per-trigger `enabled` — the master `paused` flag
  // stays false (dormant backend kill-switch, not driven from this UI).
  const saveTrigger = (index: number | null, t: Omit<Trigger, 'id' | 'enabled'>) => withBusy(async () => {
    const next: Trigger[] = index === null
      ? [...triggers, { ...t, id: crypto.randomUUID(), enabled: true }]
      : triggers.map((x, i) => (i === index ? { ...t, id: x.id ?? crypto.randomUUID(), enabled: x.enabled ?? true } : x))
    await persist({ triggers: next }); setEditingTrigger(null)
  }, 'trigger-editor')
  const deleteTrigger = (index: number) => withBusy(() => persist({ triggers: triggers.filter((_, i) => i !== index) }), 'triggers')
  // Run all / Pause all — a bulk flip of every trigger's `enabled` (operational,
  // WRITE-gated like any save). Aggregate state is derived, not stored, so the
  // buttons always reflect the real per-trigger states.
  const setAllEnabled = (enabled: boolean) => withBusy(() => persist({ triggers: triggers.map(x => ({ ...x, enabled })) }), 'triggers')
  // Per-trigger run/pause.
  const toggleTriggerEnabled = (index: number, enabled: boolean) => withBusy(() =>
    persist({ triggers: triggers.map((x, i) => (i === index ? { ...x, enabled } : x)) }), 'triggers')

  // ── Schedules (time-fired automation) ──────────────────────────────
  // Mirror the trigger handlers: new schedules get a stable id (keys the BullMQ
  // job-scheduler so edits reconcile); edits preserve id + enabled. `schedulesPaused`
  // is the schedules' OWN master pause — independent of the triggers' `paused`.
  const saveSchedule = (index: number | null, s: Omit<Schedule, 'id' | 'enabled'>) => withBusy(async () => {
    const next: Schedule[] = index === null
      ? [...schedules, { ...s, id: crypto.randomUUID(), enabled: true }]
      : schedules.map((x, i) => (i === index ? { ...s, id: x.id ?? crypto.randomUUID(), enabled: x.enabled ?? true } : x))
    await persist({ schedules: next }); setEditingSchedule(null)
  }, 'schedule-editor')
  const deleteSchedule = (index: number) => withBusy(() => persist({ schedules: schedules.filter((_, i) => i !== index) }), 'schedules')
  const toggleScheduleEnabled = (index: number, enabled: boolean) => withBusy(() =>
    persist({ schedules: schedules.map((x, i) => (i === index ? { ...x, enabled } : x)) }), 'schedules')
  // Master pause for schedules only — writes `schedulesPaused`, never `paused`.
  const setSchedulesPausedAll = (value: boolean) => withBusy(() => persist({ schedulesPaused: value }), 'schedules')

  // Aggregate run/pause state (derived from the actual per-trigger `enabled`).
  const enabledCount = triggers.filter(t => t.enabled !== false).length
  const pausedCount = triggers.length - enabledCount
  const allRunning = triggers.length > 0 && enabledCount === triggers.length
  const allPaused = triggers.length > 0 && enabledCount === 0

  // ── Derived warnings ──────────────────────────────────────────────
  // D18: limits (the app narrowed to its granter's access below a grant) render UNDER their grant,
  // never as grants of their own (no picker, no chain warning).
  const [dragId, setDragId] = useState<string | null>(null)
  // I102: set the dragged id one frame AFTER dragstart. Setting it synchronously re-renders and
  // changes the dragged rows (and used to insert an "ungroup" strip) DURING dragstart — Chrome cancels an
  // HTML5 drag whose DOM changes in dragstart, so a grouped (or limit) row could never be dragged.
  const dragFrame = useRef<number | null>(null)
  const startDrag = (id: string) => { if (dragFrame.current !== null) cancelAnimationFrame(dragFrame.current); dragFrame.current = requestAnimationFrame(() => { dragFrame.current = null; setDragId(id) }) }
  // A drag that ends within its first frame (a quick click-release, Esc) must not leave the id
  // set by the still-pending frame (review): cancel it, then clear.
  const endDrag = () => { if (dragFrame.current !== null) { cancelAnimationFrame(dragFrame.current); dragFrame.current = null } setDragId(null); setDropTarget(null) }
  // Where a release would land: a grant's id (outlined), or null = the top level (its own grant).
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  // The lower of two levels ('hidden' = No access) — the most an access-only viewer may pick.
  const lowerLevel = (a: string | undefined, b: string | undefined) => (levelRank(a) <= levelRank(b) ? a : b) ?? 'readcopy'
  const limits = access.filter(a => a.limitOf)
  // I103: a limit sits where it was PLACED (`groupedUnder`) — not under whichever grant's setter
  // clamps it now (`limitOf`, re-owned by every re-plan). A place that is gone falls back to it.
  const placeOfLimit = (l: MiniAppGrant) =>
    l.groupedUnder && access.some(p => p.grantId === l.groupedUnder && !p.limitOf) ? l.groupedUnder : l.limitOf!.grantId
  const limitsOf = (g: MiniAppGrant) => limits.filter(l => placeOfLimit(l) === g.grantId)
  // Each limit is a folder ROW of its own, indented under its grant, with the SAME controls as a
  // grant row (owner, 2026-10-02: "it works the same way") — capped by its grant's chain, sharing,
  // and Remove (lifting the limit = the remover vouches for the grant's level here; server-judged).
  // Whoever can't open the folder (the blocked setter) sees it as their drive does, read-only.
  // Render FUNCTIONS, not components defined in render: a component type made per render remounts
  // its rows, which would cancel a drag in progress.
  const limitRows = (g: MiniAppGrant, depth: number) => limitsOf(g).map(l => {
    const editable = !!l.limitOf!.editable
    return (
      <li key={l.grantId} data-grant-row={l.grantId} style={{ marginLeft: depth * 24 }} className={`px-3 py-2 rounded themed-bg-secondary ${dragId === l.grantId ? 'opacity-50' : ''}`}
        draggable={editable && !accessOnly && !busy}
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'papan-app-grant'); startDrag(l.grantId) }}
        onDragEnd={endDrag}>
        <div className="flex items-center gap-2">
          {/* Drop it on a grant above to move it there; anywhere else in the list makes it its own
              grant (whoever can open the folder). */}
          {/* The handle stays put while a save / reload is in flight (only dragging pauses) — no flicker. */}
          {editable && !accessOnly && <GripVertical size={14} className={`shrink-0 themed-text-muted ${busy ? 'cursor-default' : 'cursor-grab'}`} aria-label="Drag to move" {...hintAttrs('Drag onto a folder above to move it there, or anywhere else in the list to make it its own grant')} />}
          <FileIcon name={l.folderName} isFolder size={15} className="shrink-0" />
          <span className="themed-text text-sm flex-1 truncate">{l.folderName}</span>
          {(() => {
            // Remove lifts the app to the grant's level here, sharing widens — offered only when the
            // viewer's own access allows it (the blocked setter's cap reads as read-only, like anyone's).
            const max = l.viewerMax ?? 'readcopy'
            const shown = l.permission === 'hidden' ? 'none' : l.permission
            // Lifting it gives the app what its CLAMPING grant gives here (not the row it sits under).
            const owner = access.find(x => x.grantId === l.limitOf!.grantId) ?? g
            const canLift = !accessOnly && editable && levelRank(owner.permission) <= levelRank(max)
            const canShare = editable && levelRank(max) >= levelRank(shown)
            return (
              <AccessPermissionPicker value={shown} cappedTo={!g.isSelf && !g.isInternal ? l.chainPlacement : undefined}
                disabled={busy || !editable} maxLevel={accessOnly ? lowerLevel(l.permission, max) : max} canShare={accessOnly ? false : !!l.viewerCanShare}
                onChange={(p) => { void setGrant(l, p) }}
                allowSharing={l.allowSharing} onSetSharing={canShare ? (allow) => setShare(l, allow) : undefined}
                onRemove={canLift ? () => { void removeWithGroup(l) } : undefined} removesChain={false} />
            )
          })()}
        </div>
      </li>
    )
  })

  const realAccess = access.filter(a => !a.limitOf)
  const grantedFolderIds = new Set(realAccess.map(a => a.folderId))
  const selfGrant = realAccess.find(a => a.isSelf) ?? null
  // Internal = subfolders of the app folder (e.g. the protected `_appsystem/`) — they
  // live inside the app folder already, so they NEVER need a `.chain`. Only external
  // grants do. Keep them out of externalGrants so they don't get the chain warning.
  const internalGrants = realAccess.filter(a => !a.isSelf && a.isInternal)
  const externalGrants = realAccess.filter(a => !a.isSelf && !a.isInternal)
  const chainTargetOf = (a: MiniAppGrant) => chains.find(ch => ch.targetFileId === a.folderId)
  // D20: grants GROUPED under an ancestor grant render under it and are removed with it. Drag a
  // grant (handle) onto a grant on a folder that contains it to group; onto the top strip to ungroup.
  const childrenOf = (g: MiniAppGrant) => realAccess.filter(a => a.groupedUnder === g.grantId)
  const isTop = (a: MiniAppGrant) => !a.groupedUnder || !realAccess.some(p => p.grantId === a.groupedUnder)
  const dragged = dragId ? access.find(x => x.grantId === dragId) ?? null : null
  // I105 — an ungrouped grant inside the app folder (e.g. the read-only `_appsystem/`) is SHOWN under
  // the app folder row; it is NOT stored as grouped under the self-grant, so uninstalling (removing
  // the self-grant) never cascades `_appsystem`'s read-only grant away.
  const dragCandidates = (a: MiniAppGrant) => (a.groupCandidates ?? []).filter(id => !(a.isInternal && id === selfGrant?.grantId))
  const groupUnder = (a: MiniAppGrant, under: MiniAppGrant | null) =>
    withBusy(() => miniappApi.groupGrant(folderId, a.folderId, under ? under.folderId : null))
  // Proximity (owner, 2026-10-02): a grouped grant / limit being dragged stays with its parent while
  // the pointer is still over the parent's GROUP (the parent row and everything shown under it) —
  // the parent is outlined; dragged far enough away the outline goes and a release makes it its
  // own (top-level) grant. Over another grant above it, it groups there (that row stops the event).
  const parentOfDragged = (d: MiniAppGrant): string | null => {
    const p = d.limitOf ? placeOfLimit(d) : d.groupedUnder
    return p && access.some(x => x.grantId === p && !x.limitOf) ? p : null
  }
  const groupIds = (pid: string): string[] => {
    const g = access.find(x => x.grantId === pid)
    return g ? [pid, ...limitsOf(g).map(l => l.grantId), ...childrenOf(g).flatMap(ch => groupIds(ch.grantId))] : [pid]
  }
  const NEAR_PX = 12
  const nearGroup = (list: HTMLElement, pid: string, y: number): boolean => {
    const rects = groupIds(pid).map(id => list.querySelector(`[data-grant-row="${CSS.escape(id)}"]`)?.getBoundingClientRect()).filter((r): r is DOMRect => !!r)
    if (rects.length === 0) return false
    return y >= Math.min(...rects.map(r => r.top)) - NEAR_PX && y <= Math.max(...rects.map(r => r.bottom)) + NEAR_PX
  }
  // Release ANYWHERE on the page away from the parent's group = its own grant (owner, 2026-10-02:
  // "out of the box" too). The browser cancels a drop outside every drop target, so while a grouped
  // grant / limit is dragged the WINDOW accepts it; grant rows that take the drop stop the event
  // first. Esc still cancels (no drop event).
  const accessSectionRef = useRef<HTMLElement | null>(null)
  const dragCtx = useRef<{ d: MiniAppGrant | null; parent: string | null }>({ d: null, parent: null })
  dragCtx.current = { d: dragged, parent: dragged ? parentOfDragged(dragged) : null }
  const nearRef = useRef(nearGroup); nearRef.current = nearGroup
  const groupUnderRef = useRef(groupUnder); groupUnderRef.current = groupUnder
  const endDragRef = useRef(endDrag); endDragRef.current = endDrag
  const draggingGrouped = !!(dragged && dragCtx.current.parent)
  useEffect(() => {
    if (!draggingGrouped) return
    const near = (y: number) => { const el = accessSectionRef.current; const { parent } = dragCtx.current; return !!(el && parent && nearRef.current(el, parent, y)) }
    const onOver = (e: DragEvent) => {
      e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      setDropTarget(near(e.clientY) ? dragCtx.current.parent : null)
    }
    const onDrop = (e: DragEvent) => {
      const { d } = dragCtx.current
      if (!d) return
      e.preventDefault()
      const stays = near(e.clientY)
      endDragRef.current()
      if (!stays) void groupUnderRef.current(d, null)
    }
    window.addEventListener('dragover', onOver)
    window.addEventListener('drop', onDrop)
    return () => { window.removeEventListener('dragover', onOver); window.removeEventListener('drop', onDrop) }
  }, [draggingGrouped])
  // The confirm names what THIS viewer can see grouped under it; grants on folders hidden from them
  // may go too (only ever a shrink — a widen through the cascade is judged and refused).
  // Remove (owner, 2026-10-02): no "also removes the grouped ones" prompt — the confirm appears only
  // when OTHER editors would lose the ability to edit this app (they can't open what the removal
  // opens to it), named, from a dry run of the very removal. The remover never loses it: a removal
  // that would stop them editing is refused (D23 (a), owner 2026-10-03 — no "give up editing"); a
  // refused removal skips the prompt and shows its refusal.
  const removeWithGroup = async (a: MiniAppGrant) => {
    // A dry run that FAILED (network, 5xx) can't say who'd lose edit access — ask anyway
    // rather than silently skip the warning (see planRevokeConfirm).
    const plan = await planRevokeConfirm(() => miniappApi.previewRevoke(folderId, a.folderId))
    if (plan.kind !== 'none') {
      const who = plan.kind === 'losing' ? plan.names : []
      const list = who.length === 1 ? who[0] : `${who.slice(0, -1).join(', ')} and ${who[who.length - 1]}`
      const message = plan.kind === 'losing'
        ? `After this, ${list} will no longer be able to edit this app — the app would reach folders they don’t have access to.`
        : 'Couldn’t check who would lose edit access to this app. Remove anyway?'
      const ok = confirmDialog
        ? await confirmDialog({ title: `Remove “${a.folderName}”?`, message, type: 'warning', confirmText: 'Remove' })
        : window.confirm(message)
      if (!ok) return
    }
    void removeGrant(a)
  }
  // A folder this app's own chains don't bring in, reached (or not) through a chain DEEPER in the app (#1235):
  // 'none' — no usable route the VIEWER can see (none, or only a chain that Needs approval / the app can't use —
  //   placement none; a chain hidden from the viewer isn't counted, D20 — so the text claims nothing about reach);
  // 'capped' — reached below the grant; 'full' — the deeper chain already gives the full grant (no warning).
  const deepRoute = (a: { permission: string; chainPlacement?: string }): 'none' | 'capped' | 'full' => {
    const placed = levelRank(a.chainPlacement)
    if (placed === 0) return 'none'
    return levelRank(a.permission) > placed ? 'capped' : 'full'
  }
  const grantRow = (a: MiniAppGrant, depth: number): ReactNode => {
    const external = !a.isSelf && !a.isInternal
    const canDrop = !!dragged && dragged.grantId !== a.grantId && dragCandidates(dragged).includes(a.grantId)
    const outlined = dropTarget === a.grantId
    // Whether the row HAS a handle — independent of `busy`, so a reload doesn't blink it away.
    // I105: a grant INSIDE the app folder already shows under the app folder row, so the app's own
    // grant is not somewhere to drag it — no handle when that is its only candidate.
    // Regrouping is a manager's change — "lower or remove only" viewers get no handle (review B6).
    const canDrag = !accessOnly && dragCandidates(a).length > 0
    return (
      <Fragment key={a.grantId}>
        <li data-grant-row={a.grantId} style={{ marginLeft: depth * 24 }} className={`px-3 py-2 rounded themed-bg-secondary ${outlined ? 'ring-1 ring-blue-400' : ''} ${dragId === a.grantId ? 'opacity-50' : ''}`}
          draggable={canDrag && !busy}
          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'papan-app-grant'); startDrag(a.grantId) }}
          onDragEnd={endDrag}
          onDragOver={(e) => { if (canDrop) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDropTarget(a.grantId) } }}
          onDrop={(e) => { if (!canDrop) return; e.preventDefault(); e.stopPropagation(); const d = dragged; endDrag(); if (d) void groupUnder(d, a) }}>
          <div className="flex items-center gap-2">
            {canDrag && <GripVertical size={14} className={`shrink-0 themed-text-muted ${busy ? 'cursor-default' : 'cursor-grab'}`} aria-label="Drag to group" {...hintAttrs('Drag onto a folder above to group it under that one (removing it removes this too), or anywhere else in the list to ungroup')} />}
            <FileIcon name={a.folderName} isFolder size={15} className="shrink-0" />
            <span className="themed-text text-sm flex-1 truncate">{a.folderName}</span>
            {/* Chain-reached grant (its own chain or an ancestor's): capped only by the app's own level on the
                chain row (placement — #1235). Re-sharing + Remove access live inside the dropdown. */}
            <AccessPermissionPicker value={a.permission === 'hidden' ? 'none' : a.permission} cappedTo={external ? a.chainPlacement : undefined} disabled={busy} maxLevel={accessOnly ? lowerLevel(a.permission, a.viewerMax) : a.viewerMax} canShare={accessOnly ? false : a.viewerCanShare !== false} onChange={(p) => setGrant(a, p)} allowSharing={a.allowSharing} onSetSharing={accessOnly && !a.allowSharing ? undefined : (allow) => setShare(a, allow)} onRemove={() => removeWithGroup(a)} removesChain={!!chainTargetOf(a)} />
          </div>
          {/* No chain warning when a chain already brings in this folder or an ancestor, or it is
              inside the app folder (owner, 2026-10-02). A folder reached only through a chain DEEPER in the app has
              a cap (`chainPlacement`) and says so (#1235) — the app does reach it, at that level. */}
          {external && !a.coveredByChain && deepRoute(a) !== 'full' && (
            <div className="flex items-center gap-1.5 mt-1 ml-6 text-[11px] text-amber-400">
              <AlertTriangle size={12} className="shrink-0" />
              <span>{deepRoute(a) === 'capped'
                ? 'Reached only through a chain deeper in the app, at its capped level — add a chain for the full grant.'
                : 'No chain in the app folder brings this folder in — add one for the full grant.'}</span>
              <button onClick={() => addChain(a.folderId)} disabled={busy} className="underline hover:text-amber-300 disabled:opacity-40">Add chain</button>
            </div>
          )}
        </li>
        {limitRows(a, depth + 1)}
        {childrenOf(a).map(ch => grantRow(ch, depth + 1))}
      </Fragment>
    )
  }
  const ownFolderName = selfGrant?.folderName ?? folderName ?? 'This PDApp'

  // Not installed yet: a `.miniapp` FOLDER exists, but there's no app principal
  // until it's installed. The install consent point lives on the RUNNER now, so
  // settings suppresses this screen and renders normally (own folder = No Access).
  if (loaded && !denied && !selfGrant && !suppressInstallGate) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 themed-bg-primary">
        <div className="max-w-md text-center">
          <ShieldCheck size={30} className="mx-auto themed-icon mb-3" />
          <h2 className="text-base font-semibold themed-text mb-1.5">“{ownFolderName}” needs your permission to run</h2>
          <p className="text-sm themed-text-secondary mb-2">
            Allowing it turns this folder into a running <strong>PDApp</strong> — a service principal that runs your
            triggers and scripts with <strong>write access to this folder</strong>. Its config folder
            (<code>_appsystem/</code>) stays <strong>read-only</strong> to the running app, so it can’t rewrite its own automation.
          </p>
          <p className="text-xs themed-text-muted mb-5">
            The app reaches other folders through the access list below (chains), capped by your own access — and through a chain, by its own access to the chain in its folder. Until you allow it, the app doesn’t run.
          </p>
          <button
            onClick={install}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-4 py-2 rounded disabled:opacity-40"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Allow
          </button>
          {(error ?? loadError) && <div className="text-xs text-red-400 mt-3">{error ?? loadError}</div>}
        </div>
      </div>
    )
  }

  // Owner, 2026-10-03: whoever can't EDIT the app gets an overlay over its settings — live (it
  // follows every reload, which realtime access changes trigger). A manager of the app folder may
  // still step past it to lower / remove access (stopping an app stays open to them, I87).
  const lockedOut = !!denied || (loaded && !canManageApp)
  // Owner, 2026-10-03: past the overlay, someone who may only LOWER / REMOVE the app's access sees
  // just that — the access list, every menu capped at the current level, nothing else.
  const accessOnly = loaded && !canManageApp && !denied
  const showOverlay = lockedOut && !(overlayDismissed && !denied)
  const overlayThemeClass = getThemeClass(settings.theme.workspace)
  return (
    <div className="relative flex-1 flex flex-col min-h-0">
    {showOverlay && (
      <div className="absolute inset-0 z-30 flex items-start justify-center pt-[16vh] px-4 bg-black/25 backdrop-blur-[3px]" data-testid="pdapp-locked-overlay">
        <div className={`${overlayThemeClass} ws-glass-modal ws-anim-modal-in w-full max-w-md`}>
          <div className="px-5 pt-5 pb-4 flex items-start gap-3">
            <Lock size={20} className="shrink-0 mt-0.5 themed-text-muted" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold themed-text">You can’t edit this app</h3>
              <p className="mt-1.5 text-sm leading-relaxed themed-text-secondary">
                {denied
                  ? 'You need write access on this PDApp folder — and at least the app’s own access on every folder and keybook key it can reach.'
                  : 'The app now reaches folders or keybook keys where you have less access than it does, so you can no longer change it. Someone who can open them can still manage it.'}
              </p>
            </div>
          </div>
          {!denied && (
            <div className="ws-glass-footer flex justify-end px-5 py-3">
              <button type="button" onClick={() => setOverlayDismissed(true)} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors">
                Lower or remove access only
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    {/* `inert`, not just pointer-events-none + aria-hidden: covered content must not stay
        reachable by Tab (a focused, hidden control the keyboard can still press). */}
    <div className={`flex-1 overflow-y-auto p-6 themed-bg-primary ${showOverlay ? 'pointer-events-none select-none' : ''}`} aria-hidden={showOverlay || undefined} inert={showOverlay || undefined}>
      <div className="max-w-3xl mx-auto space-y-8">
        {!accessOnly && (<>
        {/* ── Automation consent (install wizard) — declared items awaiting a decision ── */}
        {(consentOutstanding.length > 0 || consentDecisions.length > 0) && (
          <section>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="themed-icon" />
              <h2 className="text-sm font-semibold themed-text">Automation consent</h2>
              {consentOutstanding.length > 0 && (
                <span className="text-[11px] font-medium text-amber-400">{consentOutstanding.length} awaiting</span>
              )}
            </div>
            <p className="text-xs themed-text-muted mb-3">
              Triggers the app declares don't run until you allow them. Editing what a trigger does re-asks; a denied item stays denied.
            </p>
            {error && errorArea === 'consent' && <div className="text-xs text-red-400 mb-2" role="alert" data-testid="pdapp-consent-error">{error}</div>}
            {consentOutstanding.length > 0 && (
              <ul className="space-y-1 mb-3">
                {consentOutstanding.map(item => (
                  <li key={item.contentHash} className="px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                      <ConsentItemLabel itemType={item.itemType} label={item.label} schedule={item.schedule} />
                      {item.itemType === 'key-pick' ? (
                        <>
                          {/* Chosen, not allowed: the picker is the Variables and secrets section on this page — this
                              opens that secret's editor there. */}
                          {item.pick?.name && (
                            <button
                              onClick={() => { const name = item.pick!.name; setEnvEditRequest(r => ({ name, n: (r?.n ?? 0) + 1 })) }}
                              className="text-xs px-2 py-1 rounded themed-btn-primary shrink-0"
                              aria-label={tk('appEnv.chooseKeyFor', { name: item.pick.name })}
                              data-testid="pdapp-consent-pick-choose"
                            >{tk('appEnv.chooseKey')}</button>
                          )}
                          <button onClick={() => decideConsent(item, 'denied')} disabled={busy} className="text-xs px-2 py-1 rounded themed-text-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40">{tk('appEnv.setAside')}</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => decideConsent(item, 'allowed')} disabled={busy} className="text-xs px-2 py-1 rounded themed-btn-primary disabled:opacity-40">Allow</button>
                          <button onClick={() => decideConsent(item, 'denied')} disabled={busy} className="text-xs px-2 py-1 rounded themed-text-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40">Deny</button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {consentDecisions.length > 0 && (
              <ul className="space-y-1">
                {consentDecisions.map(d => (
                  <li key={d.contentHash} className="px-3 py-2 rounded themed-bg-secondary">
                    <div className="flex items-center gap-2">
                      {d.decision === 'allowed'
                        ? <Check size={14} className={`shrink-0 ${d.paused ? 'themed-icon' : 'text-emerald-400'}`} />
                        : <Ban size={14} className="text-red-400 shrink-0" />}
                      <ConsentItemLabel itemType={d.itemType} label={d.label ?? d.contentHash.slice(0, 12)} />
                      <span className="text-[10px] uppercase tracking-wide themed-text-muted">
                        {d.decision === 'denied' ? (d.itemType === 'key-pick' ? 'set aside' : 'denied') : d.paused ? 'paused' : 'allowed'}
                      </span>
                      {d.decision === 'allowed' && (d.itemType === 'trigger' || d.itemType === 'schedule') && (
                        <button onClick={() => pauseConsentItem(d.contentHash, !d.paused)} disabled={busy} className="p-1 themed-text-muted hover:themed-text disabled:opacity-40" {...hintAttrs(d.paused ? 'Resume' : 'Pause', busy)} aria-label={d.paused ? 'Resume' : 'Pause'}>
                          {d.paused ? <Play size={14} className="text-emerald-400" /> : <Pause size={14} className="text-yellow-400" />}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* ── Icon ──────────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Palette size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Icon</h2>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            The icon shown for this PDApp across Drive — text and/or emoji on a coloured squircle.
          </p>
          <div className="flex items-center gap-3 px-3 py-2 rounded themed-bg-secondary">
            <MiniappIcon spec={iconSpec} name={ownFolderName} size={40} refId={folderId} rev={iconRev} className="rounded-lg shrink-0" />
            <span className="themed-text text-sm truncate min-w-0 flex-1">{stripMiniappFolderExt(ownFolderName)}</span>
            <button
              onClick={() => setIconEditorOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm themed-btn-secondary px-3 py-1.5 rounded shrink-0"
            >
              <Palette size={14} /> Edit icon
            </button>
          </div>
        </section>

        </>)}

        {/* ── 1. App access ─────────────────────────────────────────── */}
        {/* Proximity drop: see `accessSectionRef` (the page-wide drop zone while a grouped row is dragged). */}
        <section ref={accessSectionRef}>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">App access</h2>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            Folders this PDApp can reach as its own principal. An external grant needs a
            chain (<code>.chain</code>) in the app folder to actually appear in its workspace —
            through it the app gets the grant, capped only by its own access to that chain.
          </p>

          {loadError && <div className="text-xs text-red-400 mb-2">{loadError}</div>}
          {error && errorArea === 'access' && error !== loadError && <div className="text-xs text-red-400 mb-2">{error}</div>}
          {notice && <div className="text-xs themed-text-muted mb-2" role="status">{notice}</div>}
          {chainsIncomplete && <div className="text-xs text-amber-400 mb-2">This app has too many chains to check them all — the caps shown may be incomplete.</div>}
          {!loaded && !loadError && (
            <div className="flex items-center gap-2 text-xs themed-text-muted"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          )}

          {loaded && (
            <ul className="space-y-1 mb-3">
              {/* Own folder — always pinned first, never deletable. The permission
                  dropdown doubles as the grant control: 'No Access' when ungranted,
                  picking a level grants the app access to its own folder. */}
              <li data-grant-row={selfGrant?.grantId} className={`px-3 py-2 rounded themed-bg-secondary ${selfGrant && dropTarget === selfGrant.grantId ? 'ring-1 ring-blue-400' : ''}`}
                onDragOver={(e) => { if (selfGrant && dragged && dragCandidates(dragged).includes(selfGrant.grantId)) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDropTarget(selfGrant.grantId) } }}
                onDrop={(e) => { const d = dragged; if (!(selfGrant && d && dragCandidates(d).includes(selfGrant.grantId))) return; e.preventDefault(); e.stopPropagation(); endDrag(); void groupUnder(d, selfGrant) }}>
                <div className="flex items-center gap-2">
                  <FileIcon name={ownFolderName} isFolder size={15} className="shrink-0" miniappIconName={ownFolderName} miniappIconSpec={iconSpec} miniappIconRefId={folderId} miniappIconRev={iconRev} />
                  <span className="themed-text text-sm truncate min-w-0">{ownFolderName}</span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">App folder</span>
                  <div className="flex-1" />
                  <AccessPermissionPicker
                    value={selfGrant?.permission ?? 'none'}
                    disabled={busy}
                    onChange={setOwnPerm}
                    maxLevel={accessOnly ? (selfGrant?.permission ?? 'none') : undefined}
                    canShare={!accessOnly}
                    allowSharing={selfGrant?.allowSharing}
                    onSetSharing={selfGrant && !(accessOnly && !selfGrant.allowSharing) ? (allow) => setShare(selfGrant, allow) : undefined}
                  />
                </div>
              </li>
              {selfGrant && limitRows(selfGrant, 1)}
              {selfGrant && childrenOf(selfGrant).map(ch => grantRow(ch, 1))}
              {/* Internal grants (subfolders of the app folder — no chain needed) show under the app
                  folder row (I105), then external ones. */}
              {internalGrants.filter(isTop).map(a => grantRow(a, selfGrant ? 1 : 0))}
              {externalGrants.filter(isTop).map(a => grantRow(a, 0))}
            </ul>
          )}

          {!accessOnly && (
          <button onClick={() => setPickerOpen(true)} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-secondary px-3 py-1.5 rounded disabled:opacity-40">
            <Plus size={14} /> Grant folder access
          </button>
          )}
        </section>

        {/* ── Variables and secrets — the keybook owns them (a secret is a keybook key). Shown to a manager who may
            only lower / remove access too: taking a key away from the app is that (the section offers them only
            that); everything else in it needs the full edit gate, which the server decides. ── */}
        <ErrorBoundary fallback={null}>
          <AppEnvSettings key={folderId} folderId={folderId} appName={stripMiniappFolderExt(ownFolderName)} reloadSignal={envReload} editRequest={envEditRequest} installed={!!selfGrant && selfGrant.permission !== 'hidden' && selfGrant.permission !== 'none'} />
        </ErrorBoundary>

        {!accessOnly && (<>
        {/* ── 2. Chains ─────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Link2 size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Chains</h2>
          </div>
          <p className="text-xs themed-text-muted mb-3">Folders chained (<code>.chain</code>) into this app’s workspace.</p>
          {loaded && chains.length === 0 && <div className="text-xs themed-text-muted italic">No chains.</div>}
          {chains.length > 0 && (
            <ul className="space-y-1">
              {chains.map(ch => {
                // Orphan chain: points at a folder the app has no grant on → warn inline, under it.
                const orphan = !!ch.targetFileId && !grantedFolderIds.has(ch.targetFileId)
                return (
                  <li key={ch.chainId} className="px-3 py-2 rounded themed-bg-secondary">
                    <div className="flex items-center gap-2">
                      <Link2 size={15} className="themed-icon shrink-0" />
                      <span className="themed-text text-sm flex-1 truncate">{ch.name}</span>
                      <span className="text-xs themed-text-muted truncate">→ {ch.targetName ?? '(missing target)'}</span>
                      <button onClick={() => removeChain(ch.chainId)} disabled={busy} className="p-1 themed-text-muted hover:text-red-400 disabled:opacity-40" {...hintAttrs('Remove chain', busy)} aria-label="Remove chain"><Trash2 size={14} /></button>
                    </div>
                    {/* Inline warning: chain whose target the app can't read. */}
                    {orphan && (
                      <div className="flex items-center gap-1.5 mt-1 ml-6 text-[11px] text-amber-400">
                        <AlertTriangle size={12} className="shrink-0" />
                        <span>The app has no access to {ch.targetName ? `“${ch.targetName}”` : 'this target'}.</span>
                        <button onClick={() => grant(ch.targetFileId!, 'readcopy')} disabled={busy} className="underline hover:text-amber-300 disabled:opacity-40">Grant access</button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* ── Saving data: only what needs a look — a run whose data wasn't saved, or saving that was slow ── */}
        {(() => {
          const sourceName = (runKey: string): string | null => {
            const [kind, ...rest] = runKey.split(':')
            const id = rest.join(':')
            if (kind === 'trigger') { const t = triggers.find((x) => x.id === id); return t ? (t.name || describeTrigger(t)) : null }
            if (kind === 'schedule') { const sc = schedules.find((x) => x.id === id); return sc ? `${sc.name || describeCadence(sc)} → ${sc.do?.script ?? ''}` : null }
            return null
          }
          // A trigger / schedule since removed has no name here; its leftover row shows only if its data is still missing.
          const attention = writesNeedingAttention(appWrites.data ?? [], (runKey) => !/^(trigger|schedule):/.test(runKey) || sourceName(runKey) !== null)
          return attention.length > 0 ? (
            <AppDatabaseWrites
              folderId={folderId}
              sources={attention}
              labelFor={sourceName}
              onOpenDebugLog={() => { setDebugLogOnRuns(true); setShowDebugLog(true) }}
            />
          ) : null
        })()}

        {/* ── 3. Triggers ───────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Triggers</h2>
            {pausedCount > 0 && (
              <span className="text-[11px] font-medium text-yellow-400">
                {allPaused ? 'all paused' : `${pausedCount} paused`}
              </span>
            )}
            <div className="flex-1" />
            {triggers.length > 0 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAllEnabled(true)}
                  disabled={busy || allRunning}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border themed-border-secondary themed-bg-tertiary themed-text themed-bg-hover disabled:opacity-40 disabled:cursor-default"
                  {...hintAttrs(allRunning ? 'All triggers are already running' : 'Resume all triggers', busy || allRunning)}
                >
                  <Play size={12} className="text-emerald-400" /> Run all
                </button>
                <button
                  onClick={() => setAllEnabled(false)}
                  disabled={busy || allPaused}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border themed-border-secondary themed-bg-tertiary themed-text themed-bg-hover disabled:opacity-40 disabled:cursor-default"
                  {...hintAttrs(allPaused ? 'All triggers are already paused' : 'Pause all triggers', busy || allPaused)}
                >
                  <Pause size={12} className="text-yellow-400" /> Pause all
                </button>
              </div>
            )}
          </div>
          <p className="text-xs themed-text-muted mb-3">
            Run something automatically when files change (<code>_appsystem/triggers.json</code>; this copy's on/off switches are kept in <code>_appsystem/settings.json</code>).
          </p>
          {triggerProblems.length > 0 && (
            <ul className="text-xs text-amber-400 mb-2 space-y-0.5">
              {triggerProblems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {error && errorArea === 'triggers' && <div className="text-xs text-red-400 mb-2" role="alert" data-testid="pdapp-triggers-error">{error}</div>}
          {loaded && triggers.length === 0 && <div className="text-xs themed-text-muted italic mb-2">No triggers yet.</div>}
          {triggers.length > 0 && (
            <ul className="space-y-1 mb-3">
              {triggers.map((t, i) => {
                const on = t.enabled !== false
                const stat = t.id ? counts[t.id] : undefined
                return (
                <li key={t.id ?? i} className="px-3 py-2 rounded themed-bg-secondary">
                  <div className="flex items-center gap-2">
                    {/* Only the content dims when paused — the action buttons stay full-opacity. */}
                    <div className={`flex items-center gap-2 flex-1 min-w-0 ${on ? '' : 'opacity-50'}`}>
                      <Zap size={14} className={`shrink-0 ${on ? 'text-emerald-400' : 'themed-icon'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="themed-text text-sm truncate">
                          {t.name || describeTrigger(t)}
                          {t.enabled === false && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-yellow-300 bg-yellow-500/20 px-1 py-px rounded">paused</span>}
                          {t.id && appWrites.data?.some((w) => w.runKey === `trigger:${t.id}` && w.outcome !== 'landed' && !w.dismissed) && (
                            <a href="#app-database-writes" className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-300 bg-amber-500/20 px-1 py-px rounded hover:underline">not saved</a>
                          )}
                        </div>
                        <div className="text-[11px] themed-text-muted truncate">
                          {t.name ? describeTrigger(t) : ''}
                          {stat && stat.count > 0
                            ? `${t.name ? ' · ' : ''}Triggered ${stat.count}× since ${new Date(stat.since).toLocaleDateString()}`
                            : `${t.name ? ' · ' : ''}Not triggered yet`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleTriggerEnabled(i, t.enabled === false)}
                      disabled={busy}
                      className="p-1 themed-text-muted hover:themed-text disabled:opacity-40"
                      {...hintAttrs(t.enabled === false ? 'Resume this trigger' : 'Pause this trigger', busy)}
                      aria-label={t.enabled === false ? 'Resume this trigger' : 'Pause this trigger'}
                    >
                      {t.enabled === false ? <Play size={14} className="text-emerald-400" /> : <Pause size={14} className="text-yellow-400" />}
                    </button>
                    <button onClick={() => setEditingTrigger({ index: i })} disabled={busy} className="text-xs themed-text-muted hover:themed-text disabled:opacity-40">Edit</button>
                    <button onClick={() => deleteTrigger(i)} disabled={busy} className="p-1 themed-text-muted hover:text-red-400 disabled:opacity-40" {...hintAttrs('Delete trigger', busy)} aria-label="Delete trigger"><Trash2 size={14} /></button>
                  </div>
                </li>
                )
              })}
            </ul>
          )}
          <button onClick={() => setEditingTrigger({ index: null })} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-secondary px-3 py-1.5 rounded disabled:opacity-40">
            <Plus size={14} /> Add trigger
          </button>
        </section>

        {/* ── Schedules ─────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Schedules</h2>
            {schedulesPaused && (
              <span className="text-[11px] font-medium text-yellow-400">all paused</span>
            )}
            <div className="flex-1" />
            {/* Master pause for schedules — writes `schedulesPaused`, independent of
                the triggers' pause. */}
            <button
              onClick={() => setSchedulesPausedAll(!schedulesPaused)}
              role="switch"
              aria-checked={schedulesPaused}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border themed-border-secondary themed-bg-tertiary themed-text themed-bg-hover disabled:opacity-40 disabled:cursor-default"
              {...hintAttrs(schedulesPaused ? 'Resume all schedules' : 'Pause all schedules', busy)}
            >
              {schedulesPaused
                ? <><Play size={12} className="text-emerald-400" /> Resume all</>
                : <><Pause size={12} className="text-yellow-400" /> Pause all</>}
            </button>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            Run a script automatically on a timer — unattended, as the app (<code>_appsystem/triggers.json</code>; this copy's on/off switches and timing are kept in <code>_appsystem/settings.json</code>).
          </p>
          {error && errorArea === 'schedules' && <div className="text-xs text-red-400 mb-2" role="alert" data-testid="pdapp-schedules-error">{error}</div>}
          {loaded && schedules.length === 0 && <div className="text-xs themed-text-muted italic mb-2">No schedules yet — add one to run a script on a timer.</div>}
          {schedules.length > 0 && (
            <ul className={`space-y-1 mb-3 ${schedulesPaused ? 'opacity-50' : ''}`}>
              {schedules.map((s, i) => {
                const on = s.enabled !== false && !schedulesPaused
                const cadence = describeCadence(s)
                const scriptPath = s.do?.type === 'python' && typeof s.do.script === 'string' ? s.do.script : ''
                const sub = [s.name ? cadence : '', scriptPath].filter(Boolean).join(' · ')
                return (
                <li key={s.id ?? i} className="px-3 py-2 rounded themed-bg-secondary">
                  <div className="flex items-center gap-2">
                    {/* Only the content dims when paused — the action buttons stay full-opacity. */}
                    <div className={`flex items-center gap-2 flex-1 min-w-0 ${s.enabled !== false ? '' : 'opacity-50'}`}>
                      <Clock size={14} className={`shrink-0 ${on ? 'text-emerald-400' : 'themed-icon'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="themed-text text-sm truncate">
                          {s.name || cadence}
                          {s.enabled === false && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-yellow-300 bg-yellow-500/20 px-1 py-px rounded">paused</span>}
                        </div>
                        {sub && <div className="text-[11px] themed-text-muted truncate">{sub}</div>}
                        {on && (() => {
                          // "Next run" only for a schedule that WILL run — the worker refuses anything
                          // not approved (paused/denied/awaiting), so say that instead.
                          const state = s.id ? scheduleConsent?.[s.id] : undefined
                          if (state === 'invalid') return <div className="text-[11px] text-red-400 truncate">Unrecognised time zone — won’t run until it’s fixed</div>
                          if (state === 'denied') return <div className="text-[11px] text-red-400 truncate">Denied — won’t run</div>
                          if (state === 'paused') return <div className="text-[11px] text-yellow-300 truncate">Paused by an approver — won’t run</div>
                          if (state === 'pending' && typeof s.cron !== 'string') return <div className="text-[11px] themed-text-muted truncate">Awaiting approval — won’t run until allowed</div>
                          return <ScheduleNextRuns cron={s.cron} tz={s.tz} className="truncate" prefix={state === 'pending' ? 'Awaiting approval — would next run' : undefined} />
                        })()}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleScheduleEnabled(i, s.enabled === false)}
                      disabled={busy}
                      className="p-1 themed-text-muted hover:themed-text disabled:opacity-40"
                      {...hintAttrs(s.enabled === false ? 'Resume this schedule' : 'Pause this schedule', busy)}
                      aria-label={s.enabled === false ? 'Resume this schedule' : 'Pause this schedule'}
                    >
                      {s.enabled === false ? <Play size={14} className="text-emerald-400" /> : <Pause size={14} className="text-yellow-400" />}
                    </button>
                    <button onClick={() => setEditingSchedule({ index: i })} disabled={busy} className="text-xs themed-text-muted hover:themed-text disabled:opacity-40">Edit</button>
                    <button onClick={() => deleteSchedule(i)} disabled={busy} className="p-1 themed-text-muted hover:text-red-400 disabled:opacity-40" {...hintAttrs('Delete schedule', busy)} aria-label="Delete schedule"><Trash2 size={14} /></button>
                  </div>
                </li>
                )
              })}
            </ul>
          )}
          <button onClick={() => setEditingSchedule({ index: null })} disabled={busy} className="inline-flex items-center gap-1.5 text-sm themed-btn-secondary px-3 py-1.5 rounded disabled:opacity-40">
            <Plus size={14} /> Add schedule
          </button>
        </section>

        {/* ── Preview ───────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <RefreshCw size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Preview</h2>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            Auto-refresh reloads the running app whenever one of its source files
            changes. Turn it off to keep the current page while you edit — files
            the app writes itself never trigger a reload either way.
          </p>
          {error && errorArea === 'preview' && <div className="text-xs text-red-400 mb-2" role="alert" data-testid="pdapp-preview-error">{error}</div>}
          <button
            onClick={toggleAutoRefresh}
            role="switch"
            aria-checked={autoRefresh}
            className="inline-flex items-center gap-2 text-sm themed-btn-secondary px-3 py-1.5 rounded"
          >
            <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${autoRefresh ? 'bg-purple-500' : 'bg-zinc-500/40'}`}>
              <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${autoRefresh ? 'translate-x-[14px]' : 'translate-x-[2px]'}`} />
            </span>
            Auto-refresh {autoRefresh ? 'on' : 'off'}
          </button>
        </section>

        {/* ── 4. Debugging ──────────────────────────────────────────── */}
        {/* This settings page only loads for callers with write on the .miniapp
            folder (the Modification Safeguard), which is exactly the debug-log
            route's own gate — no extra permission check needed here. */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Bug size={16} className="themed-icon" />
            <h2 className="text-sm font-semibold themed-text">Debugging</h2>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            The consolidated debug log of this app — console output and errors from every
            page, plus backend script errors, grouped per file.
          </p>
          <button
            onClick={() => setShowDebugLog(true)}
            className="inline-flex items-center gap-1.5 text-sm themed-btn-secondary px-3 py-1.5 rounded"
          >
            <Bug size={14} /> View debug log
          </button>
        </section>
        </>)}
      </div>

      {showDebugLog && (
        <DebugLogViewer
          file={{ id: folderId, name: ownFolderName, isFolder: true }}
          onClose={() => { setShowDebugLog(false); setDebugLogOnRuns(false) }}
          initialFilter={debugLogOnRuns ? 'runs' : undefined}
        />
      )}

      {iconEditorOpen && (
        <MiniappIconEditorDialog
          file={{
            id: folderId,
            name: ownFolderName,
            // Seed the editor with the current spec (this component has no
            // listing row, so synthesize the metadata_json the dialog parses).
            metadataJson: iconSpec ? JSON.stringify({ [MINIAPP_ICON_METADATA_KEY]: iconSpec }) : null,
          }}
          tenantId={getActiveTenantId() || undefined}
          onClose={() => setIconEditorOpen(false)}
          onSaved={(icon) => {
            setIconSpec(icon)
            // The save may have replaced an asset, which moves the rev but not the
            // spec — re-read so the pane shows the NEW image, not the cached one.
            void miniappApi.getIcon(folderId).then(({ rev }) => setIconRev(rev ?? null)).catch(() => {})
          }}
        />
      )}

      {editingTrigger && (
        <ErrorBoundary fallback={<TriggerEditorErrorModal onClose={() => setEditingTrigger(null)} />}>
          <TriggerEditor
            folderId={folderId}
            folderName={ownFolderName}
            initial={editingTrigger.index !== null ? triggers[editingTrigger.index] : null}
            busy={busy}
            saveError={errorArea === 'trigger-editor' ? error : null}
            onSave={(t) => saveTrigger(editingTrigger.index, t)}
            onClose={() => { setEditingTrigger(null); if (errorArea === 'trigger-editor') setError(null) }}
          />
        </ErrorBoundary>
      )}

      {editingSchedule && (
        <ErrorBoundary fallback={<TriggerEditorErrorModal onClose={() => setEditingSchedule(null)} />}>
          <ScheduleEditor
            folderId={folderId}
            folderName={ownFolderName}
            initial={editingSchedule.index !== null ? schedules[editingSchedule.index] : null}
            busy={busy}
            saveError={errorArea === 'schedule-editor' ? error : null}
            onSave={(s) => saveSchedule(editingSchedule.index, s)}
            onClose={() => { setEditingSchedule(null); if (errorArea === 'schedule-editor') setError(null) }}
          />
        </ErrorBoundary>
      )}

      {pickerOpen && (() => {
        const driveRoot = getRootFolderId(getActiveTenantId() ?? '')
        return (
          <DriveBrowserModal
            title="Grant a folder to the app"
            roots={[{ id: driveRoot, label: 'papan' }]}
            initialFolderId={driveRoot}
            mode="folder"
            hideItem={(f) => f.id === folderId}
            confirmLabel={busy ? 'Granting…' : 'Grant'}
            footer={
              <select value={pickerPerm} onChange={e => setPickerPerm(e.target.value)} className="themed-input text-sm px-2 py-1 rounded">
                {PERM_ORDER.map(opt => <option key={opt} value={opt}>{PERM_LABEL[opt]}</option>)}
              </select>
            }
            onConfirm={({ id }) => grant(id, pickerPerm)}
            onClose={() => setPickerOpen(false)}
          />
        )
      })()}
    </div>
    </div>
  )
}

// ─── Inline permission dropdown for an access-list grant ──────────────────
// Same portal pattern as MemberPermissionPicker. `cappedTo` (#1235): the app's own level on the chain bringing
// this folder in — through a chain the app holds min(grant, that level), so options above it are labelled capped.
function AccessPermissionPicker({ value, cappedTo, disabled, onChange, allowSharing, onSetSharing, onRemove, removesChain, allowNone = true, maxLevel, canShare = true }: {
  value: string
  /** false: "Allow sharing" disabled — the viewer has no sharing right on this folder. */
  canShare?: boolean
  /** The most this viewer may set (their own access): options above it are disabled (owner: you can't set
   *  something above your own credential). Undefined = no cap shown. */
  maxLevel?: string
  /** false: no "No Access" item (a D18 limit row — the app's route can't write a hidden grant) */
  allowNone?: boolean
  /** The app's own level on the chain row this grant is reached through (placement) — undefined: no chain cap. */
  cappedTo?: string
  disabled: boolean
  onChange: (permission: string) => void
  /** When provided, renders a RE-SHARING group (Allow / No sharing) and shows the
   *  current sharing state beneath the permission on the trigger. Own-folder rows
   *  omit it — the app doesn't re-share its own folder. */
  allowSharing?: boolean
  onSetSharing?: (allow: boolean) => void
  /** When provided, renders a danger "Remove access" item (divider above) that
   *  removes the grant. Own-folder rows omit it — they're never deletable. */
  onRemove?: () => void
  /** Whether this grant has a `.chain` that removal also deletes. External grants
   *  are chained into the app folder; internal grants (e.g. `_appsystem/`) are not, so
   *  the "Remove access" subtext must not claim to delete a chain that isn't there. */
  removesChain?: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // The shared anchored-popover placement: below the trigger, flipped ABOVE it when below can't
  // hold the menu (a row near the bottom of the panel cropped it), kept inside the viewport.
  const { pos } = useAnchoredPosition({
    open, anchorRef: triggerRef, floatingRef: dropdownRef, align: 'end',
    onDismiss: useCallback(() => setOpen(false), []),
  })

  // Placement (#1235): through a chain the app holds min(grant, its own level on the chain row) — a level is shown
  // capped only where that row level is lower.
  const showCap = (perm: string) => cappedTo !== undefined && perm !== 'none' && levelRank(perm) > levelRank(cappedTo)
  const labelOf = (perm: string) => PERM_LABEL[perm] ?? perm
  const capLabel = cappedTo === undefined || levelRank(cappedTo) === 0 ? 'No Access' : (PERM_LABEL[cappedTo] ?? cappedTo)
  const descOf = (perm: string) => showCap(perm) ? `Capped to ${capLabel} — the app’s own access to its chain.` : PERM_DESC[perm]
  const TriggerIcon = PERM_ICON[value] ?? Eye
  // Menu order mirrors the row: grantable tiers, then No Access, then the Remove action.
  const menuPerms = allowNone ? [...PERM_ORDER, 'none'] : PERM_ORDER
  const LV: Record<string, number> = { none: 0, hidden: 0, readcopy: 1, readwrite: 2, admin: 3, owner: 4 }
  const above = (opt: string) => maxLevel !== undefined && (LV[opt] ?? 0) > (LV[maxLevel] ?? 0)
  // Blue when the viewer can change something here; muted only when they can't (owner, 2026-10-02).
  const canChange = !disabled && (menuPerms.some(o => o !== value && !above(o)) || !!onRemove || !!onSetSharing)

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-1.5 py-0.5 transition-colors ${
          disabled ? 'opacity-60 cursor-default' : 'cursor-pointer themed-bg-hover'
        } ${canChange ? 'text-blue-400' : 'themed-text-muted'}`}
      >
        <span className="flex flex-col items-end leading-tight">
          <span className="inline-flex items-center gap-1.5">
            <TriggerIcon size={14} className="shrink-0" />
            <span className="whitespace-nowrap">{labelOf(value)}</span>
          </span>
          {showCap(value) && <span className="text-[10px] themed-text-muted">Capped to {capLabel}</span>}
          {onSetSharing && (
            <span className="inline-flex items-center gap-1 text-[11px] themed-text">
              <Share2 size={10} className="shrink-0" /> {allowSharing ? 'Allow sharing' : 'No sharing'}
            </span>
          )}
        </span>
        {!disabled && <ChevronDown size={14} className="themed-text-muted shrink-0" />}
      </button>
      {open && createPortal(
        <div ref={dropdownRef} className={`fixed z-[200] ${themeClass} themed-bg-secondary border themed-border-secondary rounded-md shadow-lg py-0.5 w-[240px]`} style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}>
          <div className="px-2 pt-0.5 text-[9px] themed-text-muted uppercase tracking-wider">Permission</div>
          {menuPerms.map(opt => {
            const Icon = PERM_ICON[opt] ?? Eye
            const active = value === opt
            return (
              <button
                key={opt}
                onClick={() => { if (above(opt)) return; onChange(opt); setOpen(false) }}
                disabled={above(opt)}
                className={`w-full text-left px-2 py-1.5 text-[13px] ${above(opt) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer themed-bg-hover'} ${active ? 'text-blue-400 font-medium' : 'themed-text'}`}
              >
                <div className="flex items-center gap-1.5">
                  {active ? <Check size={13} className="shrink-0" /> : <span className="inline-block w-[13px] shrink-0" />}
                  <Icon size={13} className="shrink-0" />
                  <span className="font-medium whitespace-nowrap">{labelOf(opt)}</span>
                </div>
                <div className="pl-[33px] themed-text-muted leading-tight mt-0.5 text-[11px]">{descOf(opt)}</div>
              </button>
            )
          })}
          {onSetSharing && (
            <>
              <div className="border-t themed-border my-0.5" />
              <div className="px-2 text-[9px] themed-text-muted uppercase tracking-wider flex items-center gap-1"><Share2 size={9} /> Re-sharing</div>
              {[{ v: true, label: 'Allow sharing' }, { v: false, label: 'No sharing' }].map(o => {
                const active = !!allowSharing === o.v
                // Turning sharing ON vouches for the row's level — not above the viewer's own access.
                const blocked = o.v && !active && (!canShare || (maxLevel !== undefined && (LV[maxLevel] ?? 0) < (LV[value] ?? 0)))
                return (
                  <button
                    key={o.label}
                    onClick={() => { if (blocked) return; onSetSharing(o.v); setOpen(false) }}
                    disabled={blocked}
                    className={`w-full text-left px-2 py-1.5 text-[13px] ${blocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer themed-bg-hover'} ${active ? 'text-blue-400 font-medium' : 'themed-text'}`}
                  >
                    <div className="flex items-center gap-1.5">
                      {active ? <Check size={13} className="shrink-0" /> : <span className="inline-block w-[13px] shrink-0" />}
                      <Share2 size={13} className="shrink-0" />
                      <span className="font-medium">{o.label}</span>
                    </div>
                  </button>
                )
              })}
            </>
          )}
          {onRemove && (
            <>
              <div className="border-t themed-border my-0.5" />
              <button
                onClick={() => { onRemove(); setOpen(false) }}
                className="w-full text-left px-2 py-1.5 text-[13px] cursor-pointer text-red-400 themed-bg-hover"
              >
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-[13px] shrink-0" />
                  <Trash2 size={13} className="shrink-0" />
                  <span className="font-medium">Remove access</span>
                </div>
                <div className="pl-[33px] themed-text-muted leading-tight mt-0.5 text-[11px]">{removesChain ? 'Revoke the grant and delete its chain.' : 'Revoke the grant.'}</div>
              </button>
            </>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}

// ─── Reusable drive browser (wraps the shared PickerNavigator) ────────────
// One modal for both pickers: folder mode (app access) + file mode (trigger
// `.py`, scoped to the app folder). `footer` slots the permission dropdown for
// the access case. Returns the selection + its path RELATIVE to the first root
// (derived from the breadcrumbs — used for nested script paths).
function DriveBrowserModal({ title, roots, initialFolderId, mode, hideItem, confirmLabel, footer, onConfirm, onClose }: {
  title: string
  roots: PickerRoot[]
  initialFolderId: string
  mode: 'file' | 'folder'
  hideItem?: (f: FileListItem) => boolean
  confirmLabel: string
  footer?: ReactNode
  onConfirm: (sel: { id: string; name: string; path: string }) => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootId = roots[0]?.id
  const [selId, setSelId] = useState<string | null>(null)
  const [selName, setSelName] = useState('')
  const [selIsFolder, setSelIsFolder] = useState(false)
  const [crumbs, setCrumbs] = useState<PickerCrumb[]>([])
  // Root-anchored crumb chain of the SELECTED file's own parent folder. In
  // split view the parent pane shows a different folder than `crumbs` (which
  // tracks the current/right pane), so a file picked there belongs to a
  // different chain — we can't derive its path from `crumbs`.
  const [selChain, setSelChain] = useState<PickerCrumb[]>([])

  // Resolve a file's parent chain from its parentId → the root-anchored crumb
  // list. Usually the parent is already in `crumbs` (current or parent pane) —
  // slice there. Only a search hit in an unopened folder needs a breadcrumb
  // fetch. Pure (returns the chain) so both the live selection state AND the
  // confirm path can use it without racing an async setState.
  const computeChain = useCallback(async (parentId: string | null): Promise<PickerCrumb[]> => {
    if (!parentId) return crumbs
    const idx = crumbs.findIndex(c => c.id === parentId)
    if (idx >= 0) return crumbs.slice(0, idx + 1)
    try {
      const bc = await filesApi.getBreadcrumbs(parentId)
      const ri = bc.findIndex(c => c.id === rootId)
      const rootCrumb = { id: rootId, name: roots[0]?.label ?? '' }
      return ri >= 0 ? [rootCrumb, ...bc.slice(ri + 1)] : [rootCrumb, ...bc]
    } catch { return crumbs }
  }, [crumbs, rootId, roots])
  const resolveChain = useCallback(async (parentId: string | null) => {
    setSelChain(await computeChain(parentId))
  }, [computeChain])

  // For folders (folder mode) the selection IS the current folder → use crumbs.
  // For files, use the resolved parent chain so split-view / search paths are right.
  const chainFor = (forFile: boolean) => (forFile ? selChain : crumbs)

  // Path of `fileName` relative to the first root, given its parent chain.
  const relPathFrom = (chain: PickerCrumb[], fileName: string) => {
    const i = chain.findIndex(c => c.id === rootId)
    const after = (i >= 0 ? chain.slice(i + 1) : chain).map(c => c.name)
    return [...after, fileName].join('/')
  }
  const relPath = (fileName: string) => relPathFrom(chainFor(true), fileName)
  // Human-readable path for the footer: root label down to the selection
  // (e.g. "TestApp / handlers / on_upload.py"). Includes the root,
  // unlike relPath() which is root-relative for the runner.
  const displayPath = () => {
    const forFile = mode === 'file' && !!selId && !selIsFolder
    const chain = chainFor(forFile)
    const i = chain.findIndex(c => c.id === rootId)
    // Display only (relPath keeps the real names): a drive picker's chain is told
    // from its place and may not hold the root at all.
    const parts = (i >= 0 ? chain.slice(i) : chain).map(c => readableName(c.name))
    if (forFile) parts.push(selName)
    return parts.join(' / ')
  }
  const selectedLabel = selId ? displayPath() : (mode === 'folder' ? 'Select a folder' : 'Select a file')
  const pick = (id: string, name: string, isFolder: boolean, parentId: string | null = null) => {
    setSelId(id); setSelName(name); setSelIsFolder(isFolder)
    if (!isFolder) void resolveChain(parentId)
  }
  const confirm = (id = selId, name = selName) => {
    if (!id) return
    onConfirm({ id, name, path: mode === 'folder' ? '' : relPath(name) })
  }
  // Confirm straight from a picker item (double-click) — resolve the path from
  // the item's OWN parentId rather than the possibly-not-yet-resolved selChain,
  // so a fast double-click on a search hit can't save a stale/empty path.
  const confirmFile = async (item: PickerSelection) => {
    const chain = await computeChain(item.parentId)
    onConfirm({ id: item.id, name: item.name, path: relPathFrom(chain, item.name) })
  }
  const disabled = !selId || (mode === 'folder' ? !selIsFolder : selIsFolder)

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary themed-border border rounded-lg w-[600px] h-[560px] max-h-[88vh] flex flex-col shadow-2xl`}>
        <div className="flex items-center justify-between px-4 h-11 border-b themed-border shrink-0">
          <span className="text-sm font-medium themed-text">{title}</span>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>
        <PickerNavigator
          roots={roots}
          initialFolderId={initialFolderId}
          mode={mode}
          selectedId={selId}
          onSelect={(item: PickerSelection) => pick(item.id, item.name, item.isFolder, item.parentId)}
          onConfirm={mode === 'file' ? (item: PickerSelection) => void confirmFile(item) : undefined}
          onNavigate={(fid: string | null, bc: PickerCrumb[]) => {
            setCrumbs(bc)
            if (mode !== 'folder') return
            // A list place (Projects, Shared) is no folder: nothing is picked until one opens.
            if (fid) pick(fid, bc[bc.length - 1]?.name ?? '', true)
            else setSelId(null)
          }}
          hideItem={hideItem}
          splitView
          className="flex-1"
        />
        <div className="flex items-center gap-2 px-4 h-14 border-t themed-border shrink-0">
          <div className="text-xs themed-text-muted truncate flex-1 min-w-0" title={selId ? displayPath() : undefined}>{selectedLabel}</div>
          {footer}
          <button onClick={onClose} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors shrink-0">Cancel</button>
          <button onClick={() => confirm()} disabled={disabled} className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50 shrink-0">{confirmLabel}</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ─── Trigger rule builder ─────────────────────────────────────────────────
// A friendly UI (event chips + condition rows + action) that COMPILES to the
// MongoDB-like `when` filter stored in _appsystem/triggers.json. The raw filter is
// shown (and editable) under "Advanced".

type CondKind = 'string' | 'number' | 'bool'
type SourceKey = 'file' | 'member' | 'role' | 'team' | 'invite'

const SOURCE_OPTIONS: Array<{ v: SourceKey; label: string }> = [
  { v: 'file', label: 'File' },
  { v: 'member', label: 'Member' },
  { v: 'role', label: 'Role' },
  { v: 'team', label: 'Team' },
  { v: 'invite', label: 'Invite' },
]
const SOURCE_LABEL: Record<string, string> = Object.fromEntries(SOURCE_OPTIONS.map(s => [s.v, s.label]))
const KNOWN_SOURCES: readonly SourceKey[] = SOURCE_OPTIONS.map(s => s.v)
const isSourceKey = (s: unknown): s is SourceKey => typeof s === 'string' && (KNOWN_SOURCES as readonly string[]).includes(s)

const SOURCE_EVENTS: Record<SourceKey, Array<{ v: string; label: string }>> = {
  file: [
    { v: 'file:created', label: 'Created' }, { v: 'file:updated', label: 'Updated' },
    { v: 'file:moved', label: 'Moved' }, { v: 'file:deleted', label: 'Deleted' }, { v: 'file:restored', label: 'Restored' },
  ],
  member: [{ v: 'org:member_added', label: 'Joined' }, { v: 'org:member_removed', label: 'Removed' }],
  role: [{ v: 'org:role_changed', label: 'Changed' }],
  team: [
    { v: 'team:created', label: 'Created' }, { v: 'team:updated', label: 'Updated' }, { v: 'team:deleted', label: 'Deleted' },
    { v: 'team:member_added', label: 'Member added' }, { v: 'team:member_removed', label: 'Member removed' },
  ],
  invite: [{ v: 'org:invite_changed', label: 'Changed' }],
}
const EVENT_LABEL: Record<string, string> = Object.fromEntries(Object.values(SOURCE_EVENTS).flat().map(e => [e.v, e.label]))

interface CondFieldDef { key: string; label: string; kind: CondKind; ops: string[] }
const SOURCE_FIELDS: Record<SourceKey, CondFieldDef[]> = {
  file: [
    { key: 'name', label: 'Name', kind: 'string', ops: ['contains', 'starts_with', 'ends_with', 'equals', 'regex'] },
    { key: 'ext', label: 'Extension', kind: 'string', ops: ['equals', 'is_any_of'] },
    { key: 'path', label: 'Path', kind: 'string', ops: ['starts_with', 'contains', 'regex'] },
    { key: 'size', label: 'Size (bytes)', kind: 'number', ops: ['gt', 'lt'] },
    { key: 'isFolder', label: 'Is folder', kind: 'bool', ops: ['is'] },
  ],
  member: [
    { key: 'email', label: 'Email', kind: 'string', ops: ['contains', 'ends_with', 'equals', 'regex'] },
    { key: 'role', label: 'Role', kind: 'string', ops: ['equals', 'is_any_of'] },
  ],
  role: [
    { key: 'email', label: 'Email', kind: 'string', ops: ['contains', 'ends_with', 'equals'] },
    { key: 'role', label: 'New role', kind: 'string', ops: ['equals', 'is_any_of'] },
    { key: 'previousRole', label: 'Old role', kind: 'string', ops: ['equals', 'is_any_of'] },
  ],
  team: [
    { key: 'teamName', label: 'Team name', kind: 'string', ops: ['contains', 'starts_with', 'equals', 'regex'] },
    { key: 'email', label: 'Member email', kind: 'string', ops: ['contains', 'ends_with', 'equals'] },
    { key: 'role', label: 'Member role', kind: 'string', ops: ['equals', 'is_any_of'] },
  ],
  invite: [
    { key: 'email', label: 'Email', kind: 'string', ops: ['contains', 'ends_with', 'equals', 'regex'] },
  ],
}
const OP_LABEL: Record<string, string> = {
  contains: 'contains', starts_with: 'starts with', ends_with: 'ends with', equals: 'is',
  regex: 'matches (regex)', is_any_of: 'is any of', gt: 'greater than', lt: 'less than', is: 'is',
}

interface CondRow { field: string; op: string; value: string }

function sourceOfEvent(ev: string): SourceKey {
  if (ev.startsWith('file:')) return 'file'
  if (ev.startsWith('team:')) return 'team'
  if (ev === 'org:role_changed') return 'role'
  if (ev === 'org:invite_changed') return 'invite'
  return 'member'
}
const fieldsByKey = (source: SourceKey) => Object.fromEntries((SOURCE_FIELDS[source] ?? []).map(f => [f.key, f]))

/** One-line human summary of a trigger for the list. Fully defensive: `when`
 *  comes from a user-editable triggers.json, so it may be any shape. */
function describeTrigger(t: Trigger): string {
  try {
    const w = (t?.when && typeof t.when === 'object' && !Array.isArray(t.when)) ? t.when as Record<string, any> : {}
    const evs: string[] = Array.isArray(w.event?.$in)
      ? w.event.$in.filter((x: unknown): x is string => typeof x === 'string')
      : (typeof w.event === 'string' ? [w.event] : [])
    const source: SourceKey = isSourceKey(w.source) ? w.source : (evs[0] ? sourceOfEvent(evs[0]) : 'file')
    const evLabel = evs.length ? evs.map(e => EVENT_LABEL[e] ?? e).join('/') : 'Any'
    const andLen = Array.isArray(w.$and) ? w.$and.length : 0
    const orLen = Array.isArray(w.$or) ? w.$or.length : 0
    const n = Object.keys(w).filter(k => k !== 'event' && k !== 'source' && !k.startsWith('$')).length + andLen + orLen
    const conds = n ? ` · ${n} condition${n > 1 ? 's' : ''}` : ''
    const act = t?.do?.type === 'python' ? `Run ${t.do.script}` : (t?.do?.type ?? '?')
    return `${SOURCE_LABEL[source] ?? source} ${evLabel}${conds} → ${act}`
  } catch { return 'Trigger' }
}

/** One-line human cadence for a schedule row ("every 5 min", "every 2 h",
 *  "at 03:00 daily"). Defensive — a hand-edited schedule may set neither field. */
function describeCadence(s: Schedule): string {
  if (typeof s.everySeconds === 'number') {
    const { n, unit } = decodeInterval(s.everySeconds) // ONE decode shared with the editor (no drift)
    return CADENCE_UNIT_LABEL[unit](n)
  }
  if (typeof s.cron === 'string' && s.cron.trim()) return describeCronSchedule(s.cron, s.tz)
  return 'No cadence set'
}

function compileCond(c: CondRow, fieldByKey: Record<string, CondFieldDef>): unknown {
  const field = fieldByKey[c.field]
  if (!field) return undefined
  const v = c.value
  if (field.kind === 'bool') return c.value === 'true'
  if (field.kind === 'number') { const n = Number(v); return Number.isFinite(n) ? (c.op === 'lt' ? { $lt: n } : { $gt: n }) : undefined }
  if (!v) return undefined
  switch (c.op) {
    // Plain string ops (no regex → no ReDoS); $regex only for the explicit op.
    case 'contains': return { $contains: v }
    case 'starts_with': return { $startsWith: v }
    case 'ends_with': return { $endsWith: v }
    case 'equals': return v
    case 'regex': return { $regex: v }
    case 'is_any_of': return { $in: v.split(',').map(s => s.trim()).filter(Boolean) }
    default: return v
  }
}

function compileWhen(source: SourceKey, events: string[], conds: CondRow[]): Record<string, unknown> {
  const fieldByKey = fieldsByKey(source)
  const f: Record<string, unknown> = { source }
  if (events.length === 1) f.event = events[0]
  else if (events.length > 1) f.event = { $in: events }
  for (const c of conds) {
    const compiled = compileCond(c, fieldByKey)
    if (compiled !== undefined) f[c.field] = compiled // last-wins per field (V1)
  }
  return f
}

/** Decode a single field condition into a builder row, or null when the builder
 *  can't faithfully represent it (wrong operator for the field kind, wrong value
 *  type, an op the field doesn't expose, etc.). Returning null bubbles up to
 *  decompileWhen → the whole filter opens as raw JSON, never a lossy edit. */
function decompileCond(field: CondFieldDef, val: unknown): CondRow | null {
  const has = (op: string) => field.ops.includes(op)
  // Operator object: exactly one operator, value-typed for the field kind.
  if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
    const ops = Object.keys(val as Record<string, unknown>)
    if (ops.length !== 1) return null
    const op = ops[0]
    const operand = (val as Record<string, unknown>)[op]
    switch (op) {
      case '$regex':      return field.kind === 'string' && has('regex')       && typeof operand === 'string' ? { field: field.key, op: 'regex',       value: operand } : null
      case '$contains':   return field.kind === 'string' && has('contains')    && typeof operand === 'string' ? { field: field.key, op: 'contains',    value: operand } : null
      case '$startsWith': return field.kind === 'string' && has('starts_with') && typeof operand === 'string' ? { field: field.key, op: 'starts_with', value: operand } : null
      case '$endsWith':   return field.kind === 'string' && has('ends_with')   && typeof operand === 'string' ? { field: field.key, op: 'ends_with',   value: operand } : null
      case '$in':
        // Builder joins on commas, so a value containing a comma can't round-trip → raw.
        if (!has('is_any_of') || !Array.isArray(operand) || !operand.every(x => typeof x === 'string' && !x.includes(','))) return null
        return { field: field.key, op: 'is_any_of', value: operand.join(', ') }
      case '$gt': return field.kind === 'number' && has('gt') && typeof operand === 'number' ? { field: field.key, op: 'gt', value: String(operand) } : null
      case '$lt': return field.kind === 'number' && has('lt') && typeof operand === 'number' ? { field: field.key, op: 'lt', value: String(operand) } : null
      case '$eq':
        // $eq is equality — normalize to the builder's scalar form (matcher treats them identically).
        if (field.kind === 'bool')   return typeof operand === 'boolean' ? { field: field.key, op: 'is',     value: String(operand) } : null
        if (field.kind === 'string') return has('equals') && typeof operand === 'string' ? { field: field.key, op: 'equals', value: operand } : null
        return null // number equality isn't a builder op (only gt/lt) → raw
      default: return null // $ne/$nin/$gte/$lte/$exists and anything unknown → raw
    }
  }
  // Scalar leaf = equality.
  if (field.kind === 'bool')   return typeof val === 'boolean' ? { field: field.key, op: 'is', value: String(val) } : null
  if (field.kind === 'number') return null // no plain-equality op for numbers in the builder → raw
  return has('equals') && typeof val === 'string' ? { field: field.key, op: 'equals', value: val } : null
}

/** Best-effort reverse of compileWhen for editing. Returns null when the filter
 *  can't be faithfully shown in the builder ($and/$or/$not, event operators,
 *  unknown source/field/op, wrong value types, …) → the editor falls back to raw
 *  JSON. Never throws: `when` is user-editable and may be any shape. */
function decompileWhen(when: unknown): { source: SourceKey; events: string[]; conds: CondRow[] } | null {
  try {
    if (when == null) return { source: 'file', events: [], conds: [] } // empty → blank builder
    if (typeof when !== 'object' || Array.isArray(when)) return null    // non-object filter → raw
    const w = when as Record<string, any>

    // event: absent, a plain string, or {$in:[string,…]} only. Any operator on
    // event (e.g. {$ne},{$regex}) is builder-inexpressible → raw (don't drop it).
    let events: string[] = []
    if ('event' in w) {
      const ev = w.event
      if (typeof ev === 'string') events = [ev]
      else if (ev !== null && typeof ev === 'object' && !Array.isArray(ev)
               && Object.keys(ev).length === 1 && Array.isArray(ev.$in)
               && ev.$in.every((x: unknown) => typeof x === 'string')) events = [...ev.$in]
      else return null
    }

    // source: explicit must be a KNOWN source; else derive from the first event.
    let source: SourceKey
    if ('source' in w) {
      if (!isSourceKey(w.source)) return null
      source = w.source
    } else {
      source = events[0] ? sourceOfEvent(events[0]) : 'file'
    }

    const fieldByKey = fieldsByKey(source)
    const conds: CondRow[] = []
    for (const [key, val] of Object.entries(w)) {
      if (key === 'source' || key === 'event') continue
      if (key.startsWith('$')) return null        // $and/$or/$not → raw
      const field = fieldByKey[key]
      if (!field) return null                      // field not valid for this source → raw
      const row = decompileCond(field, val)
      if (!row) return null                        // not faithfully representable → raw
      conds.push(row)
    }
    return { source, events, conds }
  } catch {
    return null
  }
}

/** Last-resort fallback if TriggerEditor throws (e.g. a triggers.json shape the
 *  guards didn't anticipate) — a neat modal that closes cleanly instead of
 *  white-screening the whole editor tab. */
function TriggerEditorErrorModal({ onClose }: { onClose: () => void }) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary themed-border border rounded-lg w-[420px] shadow-xl p-5`}>
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="shrink-0 text-amber-400 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-sm font-medium themed-text mb-1">Couldn’t open this trigger</h3>
            <p className="text-xs themed-text-muted">Its definition in <code>_appsystem/triggers.json</code> looks malformed. Open that file to fix it by hand, then try again.</p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="themed-btn-primary text-sm px-3 py-1.5 rounded">Close</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function TriggerEditor({ folderId, folderName, initial, busy, saveError, onSave, onClose }: {
  folderId: string
  folderName: string
  initial: Trigger | null
  busy: boolean
  /** Why the last save was refused (shown here, not behind the dialog). */
  saveError: string | null
  // The editor produces the DEFINITION only; id + enabled are operational and
  // added by saveTrigger (new → generated id + enabled; edit → preserved).
  onSave: (t: Omit<Trigger, 'id' | 'enabled'>) => void
  onClose: () => void
}) {
  // `initial.when` is user-editable (triggers.json) → decompileWhen may return
  // null ("too advanced / malformed for the builder"), in which case we open in
  // raw-JSON mode. It never throws, so the initializer is safe.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const decomp = initial ? decompileWhen(initial.when) : { source: 'file' as SourceKey, events: [], conds: [] }
  const forcedRaw = decomp === null
  const [name, setName] = useState(initial?.name ?? '')
  const [source, setSource] = useState<SourceKey>(decomp?.source ?? 'file')
  const [events, setEvents] = useState<string[]>(decomp?.events ?? [])
  const [conds, setConds] = useState<CondRow[]>(decomp?.conds ?? [])
  // `do` is also user-editable → only trust it when it's a clean python action
  // (type + string script). Anything else (a number script, a future/foreign
  // `agent` action, a missing `do`) loads blank and is flagged below so the user
  // knows saving will (re)set it to a Python script rather than silently overwrite.
  const initialScript = initial?.do?.type === 'python' && typeof initial.do.script === 'string' ? initial.do.script : ''
  const unknownAction = !!initial && !(initial.do?.type === 'python' && typeof initial.do?.script === 'string')
  const [script, setScript] = useState(initialScript)
  const [jsonMode, setJsonMode] = useState(forcedRaw)
  const [jsonText, setJsonText] = useState(() => JSON.stringify(initial?.when ?? {}, null, 2))
  const [advanced, setAdvanced] = useState(forcedRaw)
  const [scriptPickerOpen, setScriptPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Defensive lookups — `source` is always a known key here, but never let an
  // unexpected value crash the render (fall back to empty lists).
  const sourceEvents = SOURCE_EVENTS[source] ?? []
  const sourceFields = SOURCE_FIELDS[source] ?? []
  const fieldByKey = fieldsByKey(source)

  const compiledWhen = jsonMode ? null : compileWhen(source, events, conds)
  const whenPreview = jsonMode ? jsonText : JSON.stringify(compiledWhen, null, 2)

  // Switching source resets its events + conditions (fields differ per source).
  const changeSource = (s: SourceKey) => { setSource(s); setEvents([]); setConds([]) }
  const toggleEvent = (v: string) => setEvents(e => e.includes(v) ? e.filter(x => x !== v) : [...e, v])
  const addCond = () => { const f = sourceFields[0]; if (!f) return; setConds(c => [...c, { field: f.key, op: f.ops[0], value: f.kind === 'bool' ? 'false' : '' }]) }
  const setCond = (i: number, patch: Partial<CondRow>) => setConds(c => c.map((x, j) => j === i ? { ...x, ...patch } : x))
  const removeCond = (i: number) => setConds(c => c.filter((_, j) => j !== i))

  const handleSave = () => {
    setError(null)
    let when: unknown
    if (jsonMode) {
      try { when = JSON.parse(jsonText) } catch { setError('Filter JSON is invalid.'); return }
    } else {
      when = compiledWhen
    }
    if (!script.trim()) { setError('Pick a Python script to run.'); return }
    onSave({ ...(name.trim() ? { name: name.trim() } : {}), when: when as Record<string, unknown>, do: { type: 'python', script: script.trim() } })
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary themed-border border rounded-lg w-[560px] max-h-[85vh] flex flex-col shadow-xl`}>
        <div className="flex items-center justify-between px-4 h-11 border-b themed-border shrink-0">
          <span className="text-sm font-medium themed-text">{initial ? 'Edit trigger' : 'New trigger'}</span>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {forcedRaw && (
            <div className="flex items-start gap-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2.5 py-1.5">
              <AlertTriangle size={13} className="shrink-0 mt-px" />
              <span>This trigger’s filter uses features the visual builder can’t show (or was hand-edited), so it’s opened as raw JSON below. The rest still works — you can edit the JSON directly.</span>
            </div>
          )}
          {/* Name */}
          <div>
            <label className="block text-[11px] uppercase tracking-wide themed-text-muted mb-1">Name (optional)</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Reindex on CSV upload" className="themed-input text-sm px-2 py-1.5 rounded w-full" />
          </div>

          {/* When */}
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">When — source</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {SOURCE_OPTIONS.map(s => (
                <button key={s.v} onClick={() => changeSource(s.v)} disabled={jsonMode}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors disabled:opacity-40 ${source === s.v ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : 'themed-border themed-text-muted hover:themed-text'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">…and the event is</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {sourceEvents.map(e => (
                <button key={e.v} onClick={() => toggleEvent(e.v)} disabled={jsonMode}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-40 ${events.includes(e.v) ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : 'themed-border themed-text-muted hover:themed-text'}`}>
                  {e.label}
                </button>
              ))}
            </div>
            {!jsonMode && (
              <>
                <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">…and matches</div>
                <div className="space-y-1.5">
                  {conds.map((c, i) => {
                    const field = fieldByKey[c.field] ?? sourceFields[0]
                    return (
                      <div key={i} className="flex items-center gap-1.5">
                        <select value={c.field} onChange={e => { const nf = fieldByKey[e.target.value]; setCond(i, { field: e.target.value, op: nf.ops[0], value: nf.kind === 'bool' ? 'false' : '' }) }} className="themed-input text-xs px-1.5 py-1 rounded">
                          {sourceFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <select value={c.op} onChange={e => setCond(i, { op: e.target.value })} className="themed-input text-xs px-1.5 py-1 rounded">
                          {field.ops.map(op => <option key={op} value={op}>{OP_LABEL[op]}</option>)}
                        </select>
                        {field.kind === 'bool' ? (
                          <select value={c.value} onChange={e => setCond(i, { value: e.target.value })} className="themed-input text-xs px-1.5 py-1 rounded flex-1">
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : (
                          <input value={c.value} onChange={e => setCond(i, { value: e.target.value })} placeholder={c.op === 'is_any_of' ? 'a, b, c' : field.kind === 'number' ? '1024' : 'value'} className="themed-input text-xs px-2 py-1 rounded flex-1" />
                        )}
                        <button onClick={() => removeCond(i)} className="p-1 themed-text-muted hover:text-red-400" data-tooltip="Remove condition" aria-label="Remove condition"><X size={13} /></button>
                      </div>
                    )
                  })}
                </div>
                <button onClick={addCond} className="mt-1.5 inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text"><Plus size={12} /> Add condition</button>
              </>
            )}
          </div>

          {/* Do */}
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">Do</div>
            <div className="flex items-center gap-2">
              <select defaultValue="python" className="themed-input text-sm px-2 py-1.5 rounded shrink-0">
                <option value="python">Run Python script</option>
                <option value="agent" disabled>Run Agent (coming soon)</option>
              </select>
              <button
                onClick={() => setScriptPickerOpen(true)}
                title={script || undefined}
                className="flex items-center gap-2 flex-1 min-w-0 themed-input text-sm px-2 py-1.5 rounded text-left hover:themed-bg-tertiary"
              >
                <FileIcon name={script || 'script.py'} size={14} className="shrink-0" />
                <span className={`flex-1 truncate ${script ? 'themed-text' : 'themed-text-muted'}`}>{script || 'Choose .py'}</span>
              </button>
            </div>
            <p className="mt-1 text-[11px] themed-text-muted">Pick a <code>.py</code> file inside this PDApp folder — the picker is scoped to the app, so a trigger can only run a script that lives here.</p>
            {unknownAction && (
              <div className="flex items-start gap-2 mt-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2.5 py-1.5">
                <AlertTriangle size={13} className="shrink-0 mt-px" />
                <span>This trigger’s action wasn’t a recognized Python script (hand-edited or a future action type). Choose a <code>.py</code> file above — saving will set the action to that script.</span>
              </div>
            )}
          </div>

          {/* Advanced */}
          <div>
            <button onClick={() => setAdvanced(a => !a)} className="inline-flex items-center gap-1 text-[11px] themed-text-muted hover:themed-text">
              <ChevronDown size={12} className={`transition-transform ${advanced ? '' : '-rotate-90'}`} /> Advanced (filter JSON)
            </button>
            {advanced && (
              <div className="mt-2">
                <label className="flex items-center gap-1.5 text-[11px] themed-text-muted mb-1.5 cursor-pointer">
                  <input type="checkbox" checked={jsonMode} onChange={e => {
                    if (e.target.checked) {
                      // Builder → raw: seed the textarea from the compiled filter.
                      setJsonText(JSON.stringify(compiledWhen ?? {}, null, 2)); setJsonMode(true); setError(null)
                    } else {
                      // Raw → builder: only switch if the JSON both parses AND is
                      // fully representable. Otherwise stay in JSON and explain why
                      // — never silently discard or reset the user's filter.
                      let parsed: unknown
                      try { parsed = JSON.parse(jsonText) } catch { setError('Filter JSON is invalid — fix it, or keep editing as JSON.'); return }
                      const d = decompileWhen(parsed)
                      if (!d) { setError('This filter uses features the builder can’t show — keep editing as JSON.'); return }
                      setSource(d.source); setEvents(d.events); setConds(d.conds); setJsonMode(false); setError(null)
                    }
                  }} />
                  Edit as JSON
                </label>
                <textarea value={whenPreview} readOnly={!jsonMode} onChange={e => setJsonText(e.target.value)}
                  className="themed-input text-xs font-mono px-2 py-1.5 rounded w-full h-32 resize-none" spellCheck={false} />
              </div>
            )}
          </div>

          {(error ?? saveError) && <div className="text-xs text-red-400" role="alert">{error ?? saveError}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 h-14 border-t themed-border shrink-0">
          <button onClick={onClose} className="themed-btn-secondary text-sm px-3 py-1.5 rounded">Cancel</button>
          <button onClick={handleSave} disabled={busy} className="themed-btn-primary text-sm px-3 py-1.5 rounded disabled:opacity-40">{busy ? 'Saving…' : 'Save trigger'}</button>
        </div>
      </div>

      {scriptPickerOpen && (
        <DriveBrowserModal
          title="Pick a Python script"
          roots={[{ id: folderId, label: folderName }]}
          initialFolderId={folderId}
          mode="file"
          hideItem={(f) => !f.isFolder && !f.name.endsWith('.py')}
          confirmLabel="Select"
          onConfirm={({ path }) => { setScript(path); setScriptPickerOpen(false) }}
          onClose={() => setScriptPickerOpen(false)}
        />
      )}
    </div>,
    document.body,
  )
}

// ─── Schedule editor ──────────────────────────────────────────────────────
// Mirrors TriggerEditor's modal chrome + script picker. A schedule fires on a
// cadence rather than an event: exactly ONE of an interval ("every N units",
// compiled to `everySeconds`) or a 5-field `cron` string. The action is the same
// python-script union as a trigger, scoped to the app folder via the picker.
type CadenceUnit = 'minutes' | 'hours' | 'days'
const CADENCE_UNIT_SECONDS: Record<CadenceUnit, number> = { minutes: 60, hours: 3600, days: 86400 }
const CADENCE_UNIT_LABEL: Record<CadenceUnit, (n: number) => string> = {
  minutes: (n) => `every ${n} min`, hours: (n) => `every ${n} h`, days: (n) => `every ${n} day${n > 1 ? 's' : ''}`,
}

function decodeInterval(everySeconds?: number): { n: number; unit: CadenceUnit } {
  if (typeof everySeconds !== 'number' || everySeconds <= 0) return { n: 5, unit: 'minutes' }
  if (everySeconds % 86400 === 0) return { n: everySeconds / 86400, unit: 'days' }
  if (everySeconds % 3600 === 0) return { n: everySeconds / 3600, unit: 'hours' }
  return { n: Math.max(1, Math.round(everySeconds / 60)), unit: 'minutes' }
}

function ScheduleEditor({ folderId, folderName, initial, busy, saveError, onSave, onClose }: {
  folderId: string
  folderName: string
  initial: Schedule | null
  busy: boolean
  /** Why the last save was refused (shown here, not behind the dialog). */
  saveError: string | null
  // The editor produces the DEFINITION only; id + enabled are operational and
  // added by saveSchedule (new → generated id + enabled; edit → preserved).
  onSave: (s: Omit<Schedule, 'id' | 'enabled'>) => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const initInterval = decodeInterval(initial?.everySeconds)
  const [name, setName] = useState(initial?.name ?? '')
  const [mode, setMode] = useState<'interval' | 'cron'>(typeof initial?.cron === 'string' ? 'cron' : 'interval')
  const [intervalN, setIntervalN] = useState(String(initInterval.n))
  const [intervalUnit, setIntervalUnit] = useState<CadenceUnit>(initInterval.unit)
  const [cron, setCron] = useState(initial?.cron ?? '0 3 * * *')
  // The zone the cron's clock times mean. A NEW cron (a new schedule, or an interval being
  // turned into a cron) defaults to the author's own zone — typing "9" means nine on their
  // clock; an existing cron shows what it runs in today — its `tz`, or UTC when it has none.
  const [tz, setTz] = useState(initial?.cron != null ? scheduleTimeZone(initial) : (browserTimeZone() ?? SCHEDULE_DEFAULT_TZ))
  const zoneOptions = useMemo(() => {
    let all: string[] = []
    try { all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [] } catch { /* older engine */ }
    return Array.from(new Set([SCHEDULE_DEFAULT_TZ, ...(browserTimeZone() ? [browserTimeZone() as string] : []), tz, ...all]))
  }, [tz])
  // `do` is user-editable → only trust a clean python action; anything else loads
  // blank and is flagged so the user knows saving (re)sets it to a python script.
  const initialScript = initial?.do?.type === 'python' && typeof initial.do.script === 'string' ? initial.do.script : ''
  const unknownAction = !!initial && !(initial.do?.type === 'python' && typeof initial.do?.script === 'string')
  const [script, setScript] = useState(initialScript)
  const [scriptPickerOpen, setScriptPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = () => {
    setError(null)
    const scr = script.trim()
    if (!scr) { setError('Pick a Python script to run.'); return }
    if (!scr.endsWith('.py')) { setError('The script must be a .py file.'); return }
    let cadence: { everySeconds: number } | { cron: string; tz?: string }
    if (mode === 'interval') {
      const num = Number(intervalN)
      if (!Number.isFinite(num) || num <= 0) { setError('Enter a positive interval.'); return }
      const secs = Math.round(num * CADENCE_UNIT_SECONDS[intervalUnit])
      if (secs < MIN_SCHEDULE_SECONDS) { setError(`The minimum interval is ${MIN_SCHEDULE_SECONDS} seconds.`); return }
      cadence = { everySeconds: secs }
    } else {
      const c = cron.trim()
      if (!isPlausibleCron(c)) { setError('Cron must be 5 fields (m h dom mon dow) — no seconds field.'); return }
      if (!isValidTimeZone(tz)) { setError('Pick a valid time zone.'); return }
      // An existing zone-less schedule left on UTC stays zone-less: writing `tz: "UTC"` would
      // mean the same thing but change its approval hash, re-asking for an unrelated edit.
      cadence = initial?.cron != null && initial.tz == null && tz === SCHEDULE_DEFAULT_TZ ? { cron: c } : { cron: c, tz }
    }
    onSave({ ...(name.trim() ? { name: name.trim() } : {}), ...cadence, do: { type: 'python', script: scr } })
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative themed-bg-secondary themed-border border rounded-lg w-[560px] max-h-[85vh] flex flex-col shadow-xl`}>
        <div className="flex items-center justify-between px-4 h-11 border-b themed-border shrink-0">
          <span className="text-sm font-medium themed-text">{initial ? 'Edit schedule' : 'New schedule'}</span>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-[11px] uppercase tracking-wide themed-text-muted mb-1">Name (optional)</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Nightly sync" className="themed-input text-sm px-2 py-1.5 rounded w-full" />
          </div>

          {/* Cadence */}
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">Cadence</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {([{ v: 'interval', label: 'Every N' }, { v: 'cron', label: 'Cron' }] as const).map(o => (
                <button key={o.v} onClick={() => setMode(o.v)}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${mode === o.v ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : 'themed-border themed-text-muted hover:themed-text'}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {mode === 'interval' ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs themed-text-muted">Every</span>
                <input type="number" min={1} value={intervalN} onChange={e => setIntervalN(e.target.value)} className="themed-input text-sm px-2 py-1.5 rounded w-20" />
                <select value={intervalUnit} onChange={e => setIntervalUnit(e.target.value as CadenceUnit)} className="themed-input text-sm px-2 py-1.5 rounded">
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
              </div>
            ) : (
              <div>
                <input value={cron} onChange={e => setCron(e.target.value)} placeholder="0 3 * * *" spellCheck={false} className="themed-input text-sm font-mono px-2 py-1.5 rounded w-full" />
                <p className="mt-1 text-[11px] themed-text-muted">Five fields — <code>m h dom mon dow</code>. No seconds field (sub-minute schedules aren’t allowed).</p>
                <label className="block text-[11px] uppercase tracking-wide themed-text-muted mt-3 mb-1">Time zone</label>
                <select value={tz} onChange={e => setTz(e.target.value)} className="themed-input text-sm px-2 py-1.5 rounded w-full">
                  {zoneOptions.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
                {isPlausibleCron(cron.trim()) && isValidTimeZone(tz) && (
                  <ScheduleNextRuns cron={cron.trim()} tz={tz} count={5} className="mt-2" />
                )}
              </div>
            )}
            <p className="mt-1.5 text-[11px] themed-text-muted">Minimum interval is {MIN_SCHEDULE_SECONDS} seconds. Schedules run unattended, as the app.</p>
          </div>

          {/* Do */}
          <div>
            <div className="text-[11px] uppercase tracking-wide themed-text-muted mb-1.5">Do</div>
            <div className="flex items-center gap-2">
              <select defaultValue="python" className="themed-input text-sm px-2 py-1.5 rounded shrink-0">
                <option value="python">Run Python script</option>
                <option value="agent" disabled>Run Agent (coming soon)</option>
              </select>
              <button
                onClick={() => setScriptPickerOpen(true)}
                title={script || undefined}
                className="flex items-center gap-2 flex-1 min-w-0 themed-input text-sm px-2 py-1.5 rounded text-left hover:themed-bg-tertiary"
              >
                <FileIcon name={script || 'script.py'} size={14} className="shrink-0" />
                <span className={`flex-1 truncate ${script ? 'themed-text' : 'themed-text-muted'}`}>{script || 'Choose .py'}</span>
              </button>
            </div>
            <p className="mt-1 text-[11px] themed-text-muted">Pick a <code>.py</code> file inside this PDApp folder — the picker is scoped to the app, so a schedule can only run a script that lives here.</p>
            {unknownAction && (
              <div className="flex items-start gap-2 mt-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2.5 py-1.5">
                <AlertTriangle size={13} className="shrink-0 mt-px" />
                <span>This schedule’s action wasn’t a recognized Python script (hand-edited or a future action type). Choose a <code>.py</code> file above — saving will set the action to that script.</span>
              </div>
            )}
          </div>

          {(error ?? saveError) && <div className="text-xs text-red-400" role="alert">{error ?? saveError}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 h-14 border-t themed-border shrink-0">
          <button onClick={onClose} className="themed-btn-secondary text-sm px-3 py-1.5 rounded">Cancel</button>
          <button onClick={handleSave} disabled={busy} className="themed-btn-primary text-sm px-3 py-1.5 rounded disabled:opacity-40">{busy ? 'Saving…' : 'Save schedule'}</button>
        </div>
      </div>

      {scriptPickerOpen && (
        <DriveBrowserModal
          title="Pick a Python script"
          roots={[{ id: folderId, label: folderName }]}
          initialFolderId={folderId}
          mode="file"
          hideItem={(f) => !f.isFolder && !f.name.endsWith('.py')}
          confirmLabel="Select"
          onConfirm={({ path }) => { setScript(path); setScriptPickerOpen(false) }}
          onClose={() => setScriptPickerOpen(false)}
        />
      )}
    </div>,
    document.body,
  )
}
